import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Stack,
  Typography,
  Switch,
  FormControlLabel,
  TextField,
  IconButton,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Alert,
  Tooltip,
  Card,
  CardContent,
  Autocomplete,
  Chip,
  CircularProgress,
  createFilterOptions,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import * as y from 'yup';
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  createContext,
  useContext,
} from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import DonationUploadFile from './DonationUploadFile';
import { UploadImagesComponent } from 'src/hooks/useForm';
import { getS3Url, useGetVehicleLabels } from 'src/constants';
import { LoadingButton } from '@mui/lab';
import {
  Close,
  Edit,
  Pets,
  OpenInNew,
  Refresh,
  Schedule,
  LocationOn,
  Restaurant,
  DriveEta,
  Description,
} from '@mui/icons-material';
import useOrg from 'src/hooks/useOrg';
import useAuth from 'src/hooks/useAuth';
import useVehicles from 'src/hooks/useVehicles';
import useTeamMembers from 'src/hooks/useTeamMembers';
import {
  AddressInputWithoutMap,
  DateComponent,
  DayOfTheMonthSelector,
  buildDonationWriteFields,
  TimeComponent,
  ToggleButtonComponent,
} from 'src/hooks/useForm';
import moment from 'moment-timezone';
import { roundMinutes } from 'src/utils/time';
import useDaysOfTheWeek from 'src/hooks/useDaysOfTheWeek';
import {
  parseDonation,
  calculateDatesForCopy,
  useAddDonationSchema,
  useGetDaysOfTheWeekValidator,
  useMultipleDriversSchema,
  getDaysOfTheWeekFormValue,
  isDonationReceiverRequired,
} from 'src/hooks/donationFormHelpers';
import DonationFoodTypesSelector, {
  useFoodTypesSelector,
  useGenerateFoodTypesObj,
} from './DonationFoodTypesSelector';
import BetaChip from './BetaChip';
import DonationScheduleEnd from './DonationScheduleEnd';
import useDriverAvailability from 'src/hooks/useDriverAvailability';
import { findFirstInvalidStep } from 'src/utils/donationStepValidation';
import {
  buildInventoryAnalysisItems,
  buildInventoryDonationPrefill,
  Distance,
  formatDisplayDate,
  mergeInventoryDonationAnalysis,
  retainInventoryPhotosForDonation,
  selectInventoryPhotosForDonation,
  Weight,
} from 'careit';
import { useMeasureFieldUnits } from './MeasureField';
import { useCheckAction } from 'src/utils/permissions';
import _isNumber from 'lodash/isNumber';
import getTimeZoneIdFromCoords from 'src/utils/getTimeZoneIdFromCoords';
import { parseJsonArray, parseJsonObject } from 'src/utils/json';
import { v4 as uuidv4 } from 'uuid';
import { validateImageDecodable } from 'src/utils/validateImageDecodable';
import { uploadImages } from 'src/components/form/UploadImages';
import {
  processRecurringIntervalData,
  getScheduleEndDate,
} from 'src/utils/recurring';
import * as backend from 'src/backend';
import { useSnackbar } from 'notistack';
import {
  processFoodRunFormData,
  useAddFoodRunSchema,
} from 'src/views/schedule/FoodRun/helper';
import useEventBus from 'src/hooks/useEventBus';
import SocketIO from 'src/backend/SocketIO';
import useOrgsForFoodRunCreation from 'src/hooks/useOrgsForFoodRunCreation';
import {
  buildFoodRunRecipientMatchRequest,
  capRecipientOptions,
  getRecipientMatchGroupLabel,
  getRecipientMatchLabels,
  getRecipientMatchNote,
  getRecipientMoreHint,
} from 'src/utils/foodRunRecipientMatching';
import {
  RecipientMatchCaption,
  RecipientMoreHint,
  renderRecipientMatchGroup,
} from 'src/components/RecipientMatchDisplay';
import TaxExemptBadge from 'src/components/TaxExemptBadge';
import { TAX_EXEMPT_STATUS, getTaxExemptStatus } from 'src/utils/einStatus';
import MultipleDriversSelector from './MultipleDriversSelector';
import SingleDriverSelector from './SingleDriverSelector';
import {
  ENABLE_CREATE_DONATION_PARTIAL,
  ENABLE_MULTIPLE_DRIVERS,
} from 'src/config';
import { Link } from 'react-router-dom';
import { PATH_DASHBOARD } from 'src/routes/paths';
import _ from 'lodash';
import { readFile } from 'src/utils/file';
import { useHistory } from 'react-router-dom';
import LoadingScreen from './LoadingScreen';
import useFoodAnalysis from 'src/hooks/useFoodAnalysis';
import _get from 'lodash/get';
import { useTourContext } from 'src/components/TourProvider';
import * as tourAPI from 'src/backend/tour';
import { useInventoryItems } from 'src/hooks/useInventoryItems';
import InventoryDonationSourceStep from './InventoryDonationSourceStep';

// Tour configuration for the create donation dialog
const CREATE_DONATION_TOUR_KEY = 'create_donation_dialog_seen';
const CREATE_FOOD_RUN_TOUR_KEY = 'create_food_run_dialog_seen';
const POST_DONATION_TOUR_KEY = 'post_donation_guide';
const POST_DONATION_TOUR_PENDING_STEP = 999;
const keyboardCardButtonSx = {
  appearance: 'none',
  background: 'transparent',
  color: 'inherit',
  font: 'inherit',
  textAlign: 'center',
  '&:focus-visible': {
    outline: '2px solid',
    outlineColor: 'secondary.main',
    outlineOffset: 2,
  },
};
const getStoredImageUrl = (image) =>
  /^https?:\/\//i.test(image) ? image : getS3Url(image);
const matchesStoredImage = (image, sourceKey) => {
  if (typeof image !== 'string' || !sourceKey) return false;
  const withoutQuery = image.split('?')[0];
  return withoutQuery === sourceKey || withoutQuery.endsWith(`/${sourceKey}`);
};
const createDonationTourSteps = [
  {
    title: 'tours.donations-create.welcome.title',
    content: 'tours.create-donation.welcome.content',
    placement: 'center',
  },
  {
    target: '#dialog-create-donation',
    title: 'tours.create-donation.how-it-works.title',
    content: 'tours.create-donation.how-it-works.content',
    placement: 'top',
  },
];

const foodRunCreateTourSteps = [
  {
    title: 'donations.create-food-run',
    content: 'tours.create-food-run.welcome.content',
    placement: 'center',
  },
  {
    target: '[data-tour="create-food-run-title-step"]',
    title: 'donations.name-your-food-run',
    content: 'tours.create-food-run.name.content',
    placement: 'top',
  },
  {
    target: '[data-tour="create-food-run-donor-step"]',
    title: 'donations.step-delivery-option-title_food-run',
    content: 'tours.create-food-run.select-donor.content',
    placement: 'top',
  },
  {
    target: '[data-tour="create-food-run-donor-step"]',
    title: 'donations.step-delivery-option-title_food-run',
    content: 'donations.step-delivery-option-description_food-run',
    placement: 'top',
  },
  {
    target: '[data-tour="create-food-run-food-types-step"]',
    title: 'donations.select-donation-categories',
    content: 'tours.create-food-run.food-types.content',
    placement: 'top',
  },
  {
    target: '[data-tour="create-food-run-logistics-step"]',
    title: 'donations.step-logistics-title',
    content: 'tours.create-food-run.logistics.content',
    placement: 'top',
  },
];

// STEP ENUM
export const STEP = {
  UPLOAD_IMAGE: 'UPLOAD_IMAGE',
  SELECT_FOOD_TYPES: 'SELECT_FOOD_TYPES',
  TITLE: 'TITLE',
  DESCRIPTION: 'DESCRIPTION',
  PARTIAL_RESCUE: 'PARTIAL_RESCUE',
  DELIVERY_METHOD: 'DELIVERY_METHOD',
  TIME_RECURRING: 'TIME_RECURRING',
  LOGISTICS: 'LOGISTICS',
  INSTRUCTIONS: 'INSTRUCTIONS',
  RECAP: 'RECAP',
};

// List of fields that must be validated per logical step
const validationByStepEnum = {
  [STEP.UPLOAD_IMAGE]: [],
  [STEP.SELECT_FOOD_TYPES]: ['foodTypeNames', 'foodTypes'],
  [STEP.TITLE]: ['title'],
  [STEP.DESCRIPTION]: ['description'],
  [STEP.DELIVERY_METHOD]: ['deliveryOption', 'pickupLocation'],
  [STEP.PARTIAL_RESCUE]: ['isPartialRescue'],
  [STEP.TIME_RECURRING]: [
    'startDate',
    'endDate',
    'startTime',
    'endTime',
    'interval',
    'dayOfTheWeek',
    'dayOfTheMonth',
  ],
  [STEP.LOGISTICS]: [
    ...(ENABLE_MULTIPLE_DRIVERS ? ['drivers'] : ['driverId']),
    'deliveryRadius',
    'estimatedRescueTimeMinutes',
    'receiverTeam',
    'donor',
    'vehicleSize',
  ],
  [STEP.INSTRUCTIONS]: ['pickupInstructions', 'dropoffInstructions'],
  [STEP.RECAP]: [],
};

const parseFoodRunForCopy = (foodRun, user, vehiclesMap, patternData) => {
  const donorLocation = parseJsonObject(foodRun.donor_location);
  const recipientLocation = parseJsonObject(foodRun.receiver_location);

  // Use common date calculation function
  const dateObj = calculateDatesForCopy(
    foodRun.start_date,
    foodRun.end_date,
    user,
    true, // isCopy is always true for food run copying
    patternData,
    foodRun.timezone_id
  );

  // Convert to preferred unit to handle unit conversions
  const foodWeightArray = parseJsonArray(foodRun.food_weight);

  const foodWeights = _.mapValues(
    _.keyBy(foodWeightArray, ({ food_type }) => _.toLower(food_type)),
    ({ estimated_weight, estimated_weight_unit }) => {
      return {
        value:
          estimated_weight === null
            ? null
            : new Weight(estimated_weight, 'lbs')
                .as(estimated_weight_unit)
                .valueOf()
                .toString(),
        unit: estimated_weight_unit,
      };
    }
  );

  let dayOfTheWeekObj = getDaysOfTheWeekFormValue(
    parseJsonArray(_get(patternData, 'days_of_the_week', '[]'))
  );
  return {
    title: foodRun.title || '',
    description: foodRun.description || '',
    foodTypes: foodWeights,
    donor: foodRun.donor_team,
    receiver: foodRun.receiver_team,
    driverId: foodRun.driver_id ? foodRun.driver_id : 'unassigned',
    vehicleSize: vehiclesMap.find((item) => foodRun.vehicle === item.vehicle)
      ?.value,
    vehicle: parseJsonObject(foodRun.vehicle_mapped).id || null,
    images: parseJsonArray(foodRun.images),
    pickup_address: {
      lat: donorLocation.lat,
      lng: donorLocation.lng,
      formattedAddress: donorLocation.formatted_address,
      name: donorLocation.place,
      streetAddress: donorLocation.street_address,
      city: donorLocation.city,
      province: donorLocation.province,
      country: donorLocation.country,
      zipCode: donorLocation.zip_code,
      placeId: donorLocation.place_id,
      timeZoneId: donorLocation.timezone_id,
    },
    dest_address: {
      lat: recipientLocation.lat,
      lng: recipientLocation.lng,
      formattedAddress: recipientLocation.formatted_address,
      name: recipientLocation.place,
      streetAddress: recipientLocation.street_address,
      city: recipientLocation.city,
      province: recipientLocation.province,
      country: recipientLocation.country,
      zipCode: recipientLocation.zip_code,
      placeId: recipientLocation.place_id,
      timeZoneId: recipientLocation.timezone_id,
    },
    custom_addresses: Boolean(foodRun.is_food_run),
    pickupInstructions: foodRun.pickup_instructions || '',
    dropoffInstructions: foodRun.dropoff_instructions || '',
    additionalImages: parseJsonArray(foodRun.additional_images),
    availableOnUnassign: patternData?.is_recurring
      ? Boolean(
          Number(
            patternData.available_on_unassign ??
              user?.available_on_unassign_default ??
              true
          )
        )
      : false,
    occurrenceType: patternData?.is_recurring
      ? patternData.end_date
        ? 'recurring'
        : 'indefinite'
      : 'onetime',
    estimatedRescueTimeMinutes: foodRun.estimated_rescue_time_minutes,
    interval: _.capitalize(foodRun.recurring_type),
    ...dateObj,
    dayOfTheWeek: parseJsonArray(_get(foodRun, 'days_of_the_week', '[]')),
    dayOfTheMonth: {
      isLastDay: !!patternData.is_last_day_of_the_month,
      customDay: patternData.day_of_the_month,
      week: {
        number: patternData.no_of_the_week_in_month,
        dayOfTheWeek: patternData.day_of_the_week_in_month,
      },
    },
    ...dayOfTheWeekObj,
    ...dateObj,
  };
};

const RepeatEverySelector = ({
  control,
  data,
  setValue,
  errors,
  updateCorrectDates,
}) => {
  const daysOfTheWeek = useDaysOfTheWeek();
  const { t } = useTranslation('translation');

  return (
    <Stack spacing={2}>
      <Controller
        control={control}
        name="interval"
        render={({ field }) => (
          <FormControl fullWidth>
            <InputLabel>{t('donations.repeat-every')}</InputLabel>
            <Select
              {...field}
              onChange={(e) => {
                field.onChange(e.target.value);
                updateCorrectDates({ newInterval: e.target.value });
              }}
              label={t('donations.repeat-every')}
            >
              <MenuItem value="Daily">{t('donations.daily')}</MenuItem>
              <MenuItem value="Weekly">{t('donations.weekly')}</MenuItem>
              <MenuItem value="Bi-weekly">{t('donations.bi-weekly')}</MenuItem>
              <MenuItem value="Monthly">{t('donations.monthly')}</MenuItem>
            </Select>
          </FormControl>
        )}
      />
      {(data.interval === 'Weekly' || data.interval === 'Bi-weekly') && (
        <Box>
          <Typography variant="body2" color="text.secondary" mb={1}>
            {t('donations.repeat-on')}
          </Typography>
          <Stack direction="row" spacing={1}>
            {[...'SMTWTFS'].map((_, i) => (
              <ToggleButtonComponent
                key={i}
                label={daysOfTheWeek[i]}
                value={data.dayOfTheWeek.includes(i)}
                onChange={() => {
                  if (data.dayOfTheWeek.includes(i)) {
                    setValue(
                      'dayOfTheWeek',
                      data.dayOfTheWeek.filter((d) => d !== i)
                    );
                    setValue(`repeat-${i}`, false);
                  } else {
                    setValue('dayOfTheWeek', [...data.dayOfTheWeek, i]);
                    setValue(`repeat-${i}`, true);
                  }
                }}
                sx={{ margin: '5px', width: '100%' }}
              />
            ))}
          </Stack>
          {errors?.dayOfTheWeek?.message && (
            <Typography mt={1} variant="body2" color="error">
              {errors?.dayOfTheWeek?.message}
            </Typography>
          )}
        </Box>
      )}
      {data.interval === 'Monthly' && (
        <Box>
          <Controller
            control={control}
            name="dayOfTheMonth"
            render={({ field }) => (
              <DayOfTheMonthSelector
                {...field}
                onChange={(value) => {
                  field.onChange(value);
                }}
                error={errors?.dayOfTheMonth?.message}
              />
            )}
          />
        </Box>
      )}
    </Stack>
  );
};

const DatesSelectors = ({ control, data, errors, setValue, isFoodRun }) => {
  const { t } = useTranslation('translation');
  return (
    <Grid container sx={{ mt: 3 }}>
      <Grid
        size={{
          sm: isFoodRun && data.occurrenceType === 'onetime' ? 12 : 6,
          xs: 12,
        }}
      >
        <Controller
          control={control}
          name="startDate"
          render={({ field }) => (
            <Box>
              <DateComponent
                {...field}
                onChange={(newDate) => {
                  field.onChange(newDate);

                  // For one-time food runs, always keep end date in sync with start date
                  if (isFoodRun && data.occurrenceType === 'onetime') {
                    setValue('endDate', newDate);
                  } else {
                    // If the new start date is after the current end date, update end date
                    if (
                      moment(newDate)
                        .startOf('day')
                        .isAfter(moment(data.endDate).startOf('day'))
                    ) {
                      setValue('endDate', newDate);
                    }
                  }
                }}
                sx={{
                  m: 0,
                  width: '100%',
                }}
                label={t(
                  data.deliveryOption === 4
                    ? 'donations.date'
                    : 'donations.start-date'
                )}
                error={!!errors?.startDate?.message}
                helperText={errors?.startDate?.message}
              />
              {errors?.startDate?.message && (
                <Typography mt={1} variant="body2" color="error">
                  {errors?.startDate?.message}
                </Typography>
              )}
            </Box>
          )}
        />
      </Grid>

      {data.deliveryOption !== 4 &&
      data.occurrenceType !== 'indefinite' &&
      !(isFoodRun && data.occurrenceType === 'onetime') ? (
        <Grid size={{ sm: 6, xs: 12 }}>
          <Controller
            control={control}
            name="endDate"
            render={({ field }) => (
              <Box>
                <DateComponent
                  {...field}
                  sx={{
                    width: '100%',
                  }}
                  minDate={data.startDate}
                  label={t('donations.end-date')}
                  error={!!errors?.endDate?.message}
                  helperText={errors?.endDate?.message}
                />
                {errors?.endDate?.message && (
                  <Typography mt={1} variant="body2" color="error">
                    {errors?.endDate?.message}
                  </Typography>
                )}
              </Box>
            )}
          />
        </Grid>
      ) : null}

      <Grid size={{ sm: 6, xs: 12 }}>
        <Controller
          control={control}
          name="startTime"
          render={({ field }) => (
            <Box>
              <TimeComponent
                {...field}
                sx={{
                  width: '100%',
                }}
                label={t(
                  data.deliveryOption === 4
                    ? 'donations.time'
                    : 'donations.start-time'
                )}
                error={!!errors?.startTime?.message}
                helperText={errors?.startTime?.message}
              />
            </Box>
          )}
        />
      </Grid>

      {data.deliveryOption !== 4 && (
        <Grid size={{ sm: 6, xs: 12 }}>
          <Controller
            control={control}
            name="endTime"
            render={({ field }) => (
              <Box>
                <TimeComponent
                  {...field}
                  sx={{
                    width: '100%',
                  }}
                  minTime={
                    moment(data.startDate)
                      .startOf('day')
                      .isSame(moment(data.endDate).startOf('day'))
                      ? data.startTime
                      : undefined
                  }
                  label={t('donations.end-time')}
                  error={!!errors?.endTime?.message}
                  helperText={errors?.endTime?.message}
                />
              </Box>
            )}
          />
        </Grid>
      )}
    </Grid>
  );
};

export function DonationScheduleFields({
  control,
  data,
  errors,
  setValue,
  isFoodRun,
  canCreateRecurring,
  user,
}) {
  const { t } = useTranslation('translation');
  const updateCorrectDates = ({
    newOccurrenceType = data.occurrenceType,
    newInterval = data.interval,
  }) => {
    if (newOccurrenceType === 'onetime') {
      if (
        data.occurrenceType !== 'onetime' ||
        !data.endDate ||
        moment(data.endDate).isSame(moment(data.startDate), 'day')
      ) {
        setValue('endDate', data.startDate);
      }
    } else {
      if (!newInterval) setValue('interval', 'Daily');
      setValue(
        'endDate',
        getScheduleEndDate({
          ...data,
          occurrenceType: newOccurrenceType,
          interval: newInterval || 'Daily',
        })
      );
    }
  };
  return (
    <Stack mt={3}>
      <CardSelector
        items={[
          {
            name: t('donations.one-time'),
            tooltip: t('donations.one-time-tooltip'),
            imageSrc: `/static/illustrations/create-donation/one-time.svg`,
            isSelected: data.occurrenceType === 'onetime',
            imageWidth: '120px',
            fontSize: '14px',
            onClick: () => {
              setValue('occurrenceType', 'onetime');
              setValue('availableOnUnassign', false);
              updateCorrectDates({ newOccurrenceType: 'onetime' });
            },
          },
          {
            name: canCreateRecurring
              ? t('donations.repeating')
              : t('donations.repeating', { context: 'plan' }),
            tooltip:
              data.deliveryOption === 4
                ? t('donations.delivery-method-tooltip')
                : t('donations.repeating-tooltip'),
            imageSrc: `/static/illustrations/create-donation/recurring.svg`,
            isSelected: data.occurrenceType !== 'onetime',
            imageWidth: '120px',
            fontSize: '14px',
            onClick: () => {
              if (data.occurrenceType !== 'onetime') return;
              setValue(
                'availableOnUnassign',
                Boolean(user?.available_on_unassign_default ?? true)
              );
              setValue('occurrenceType', 'indefinite');
              updateCorrectDates({ newOccurrenceType: 'indefinite' });
            },
            disabled: !canCreateRecurring || data.deliveryOption === 4,
          },
        ]}
      />

      <Stack mt={3}>
        {data.occurrenceType === 'onetime' ? (
          <DatesSelectors
            control={control}
            data={data}
            errors={errors}
            setValue={setValue}
            isFoodRun={isFoodRun}
          />
        ) : (
          <Stack>
            <RepeatEverySelector
              control={control}
              setValue={setValue}
              data={data}
              errors={errors}
              updateCorrectDates={updateCorrectDates}
            />
            <DonationScheduleEnd
              data={data}
              onChange={({ occurrenceType, endDate }) => {
                setValue('occurrenceType', occurrenceType);
                setValue('endDate', endDate);
              }}
            />
            <DatesSelectors
              control={control}
              data={data}
              errors={errors}
              setValue={setValue}
              isFoodRun={isFoodRun}
            />
          </Stack>
        )}
      </Stack>
    </Stack>
  );
}

const Image = ({ src, sx = {} }) => {
  return (
    <Box
      sx={{
        display: 'block',
        mx: 'auto !important',
        width: '250px',
        aspectRatio: '3/2',
        objectFit: 'contain',
        ...sx,
      }}
      component="img"
      src={src}
    />
  );
};

const CardSelector = ({ items }) => {
  return (
    <Stack
      direction={{
        xs: 'column',
        md: 'row',
      }}
      spacing={2}
      justifyContent="center"
      alignItems="center"
      sx={{ mx: 'auto', width: '100%' }}
    >
      {items.map(
        (
          {
            isSelected,
            imageSrc,
            name,
            onClick,
            imageWidth,
            fontSize = '16px',
            disabled = false,
            tooltip,
          },
          idx
        ) => {
          const content = (
            <Stack
              component="button"
              type="button"
              key={idx}
              onClick={disabled ? undefined : onClick}
              disabled={disabled}
              aria-pressed={isSelected}
              width={{ xs: '100%', md: 'auto' }}
              direction="column"
              alignItems="center"
              justifyContent="center"
              spacing={1}
              sx={{
                border: '1px solid',
                borderColor: (t) =>
                  isSelected ? t.palette.secondary.main : t.palette.divider,
                boxShadow: (t) =>
                  isSelected ? `0 0 0 1px ${t.palette.secondary.main}` : 'none',
                transition: 'border-color 0.2s, box-shadow 0.2s',
                borderRadius: '10px',
                p: 2,
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.5 : 1,
                ...keyboardCardButtonSx,
              }}
            >
              <Image
                sx={{
                  width: imageWidth || '200px',
                }}
                src={imageSrc}
              />
              <Typography
                fontWeight="bold"
                variant="body1"
                fontSize={fontSize}
                color="primary"
              >
                {name}
              </Typography>
            </Stack>
          );
          return tooltip ? (
            <Tooltip key={`tt-${idx}`} title={tooltip} arrow>
              <span>{content}</span>
            </Tooltip>
          ) : (
            content
          );
        }
      )}
    </Stack>
  );
};

const Container = ({ children, sx }) => {
  return (
    <Stack
      sx={{
        mx: 'auto',
        maxWidth: '650px',
        width: '100%',
        ...sx,
      }}
    >
      {children}
    </Stack>
  );
};

const processDonationData = ({
  donationTimeZoneId,
  destTimeZoneId,
  donationId,
  patternId,
  images,
  defaultDistanceUnit,
  user,
  isCopy = false,
  ...data
}) => {
  const {
    occurrenceType,
    isRecurring,
    interval,
    daysOfTheWeek,
    startDate,
    endDate,
    startTime,
    endTime,
    isDST,
  } = processRecurringIntervalData({
    ...data,
    timezone_id: donationTimeZoneId,
  });

  return {
    isFoodRun: false,
    occurrenceType: occurrenceType === 'onetime' ? 'onetime' : 'recurring',
    isRecurring,
    interval,
    dayOfTheMonth: data.dayOfTheMonth,
    daysOfTheWeek,
    startDate,
    endDate: occurrenceType === 'indefinite' ? null : endDate,
    startTime,
    endTime,
    isDST,
    vehicleSize: data.vehicleSize,
    vehicle:
      data.deliveryOption === 0 || data.deliveryOption === 4
        ? data.vehicle
        : null,
    deliveryOption: data.deliveryOption,
    ...buildDonationWriteFields({
      foodTypes: data.foodTypes,
      isPartialRescue: data.isPartialRescue,
      receiverTeamId:
        data.receiverTeam && data.receiverTeam !== 'unassigned'
          ? data.receiverTeam
          : null,
      destAddress:
        data.receiverTeam && data.receiverTeam !== 'unassigned'
          ? {
              ...data.destLocation,
              timeZoneId: destTimeZoneId,
              place: data.destLocation?.name,
            }
          : null,
      destinationAddresses: data.isPartialRescue
        ? data.destinationAddresses
        : [],
    }),
    title: data.title,
    description: data.description,
    pickupInstructions:
      data.deliveryOption === 1 ? data.pickupInstructions || null : null,
    dropoffInstructions:
      data.deliveryOption === 0 ? data.dropoffInstructions || null : null,
    estimatedRescueTimeMinutes: data.estimatedRescueTimeMinutes,
    pickup_address: {
      ...data.pickupLocation,
      timeZoneId: donationTimeZoneId,
      place: data.pickupLocation?.name,
    },
    images,
    additionalImages: data.additionalImages ?? [],
    inventoryItems: data.inventoryItems ?? [],
    allowInventoryOverdraft: !isCopy,
    aiEnabled: !!data.useAIToFillDetails,
    aiAnalysisResult: data.aiAnalysisResult || null,
    driverId:
      (data.deliveryOption === 0 || data.deliveryOption === 4) &&
      data.driverId !== 'unassigned'
        ? data.driverId
        : null,
    drivers:
      data.deliveryOption === 0 || data.deliveryOption === 4
        ? data.drivers
        : [],
    timezone_id: user.timezone_id,
    deliveryRadius:
      (data.deliveryOption === 0 || data.deliveryOption === 4) &&
      (!data?.receiverTeam || data.receiverTeam === 'unassigned')
        ? new Distance(
            parseFloat(data.deliveryRadius),
            defaultDistanceUnit.value
          ).as('mi')
        : null,
    donationId,
    patternId,
  };
};

const StepContext = createContext();

function useStepContext() {
  return useContext(StepContext);
}

function StepComponent({
  step,
  title,
  description,
  imageSrc,
  children,
  dataTour,
  align = 'center',
  sx = {},
}) {
  const { isStep, loading } = useStepContext();

  if (!isStep(step)) {
    return null;
  }

  if (loading) {
    children = (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
        }}
      >
        <LoadingScreen noScale size={240} />
      </Box>
    );
  }

  return (
    <Container key={step} data-tour={dataTour} sx={sx}>
      <Stack
        mt={2}
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent={align}
      >
        <Stack
          minWidth={0}
          sx={{
            ...(align === 'center' && !imageSrc ? { maxWidth: 650 } : null),
            ...(align !== 'center' ? { width: '100%' } : null),
          }}
        >
          <Typography
            fontWeight="bold"
            variant="h3"
            sx={{ minWidth: 0, overflowWrap: 'anywhere' }}
          >
            {title}
          </Typography>

          <Stack
            mt={2}
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent={align}
          >
            <Typography
              variant="body2"
              color={align === 'center' ? 'gray' : 'text.secondary'}
            >
              {description}
            </Typography>
          </Stack>
        </Stack>

        {imageSrc ? (
          <Image src={imageSrc} sx={{ display: { xs: 'none', sm: 'block' } }} />
        ) : null}
      </Stack>

      {children}
    </Container>
  );
}

const filterRecipientOptions = createFilterOptions({
  stringify: (option) => option.name || '',
});

const DialogCreateDonation = ({
  open,
  onClose: closeDialog,
  isFoodRun = false,
  invalidate,
  isRepost = false,
  isCopy = false,
  initialDonation,
  patternData = null,
  partialRescues = [],
}) => {
  const history = useHistory();
  const { t } = useTranslation('translation');
  const { user, isFoodMover, plan, invalidateUserData } = useAuth();
  const [step, setStep] = useState(0);
  const [isWaitingForAnalysis, setIsWaitingForAnalysis] = useState(false);
  const [analysisError, setAnalysisError] = useState(null);
  const [corruptedImageIndices, setCorruptedImageIndices] = useState([]);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [hasReachedSummary, setHasReachedSummary] = useState(false);
  const [donorsList, setDonorsList] = useState([]);
  const [recipientsList, setRecipientsList] = useState([]);
  const [pickupManual, setPickupManual] = useState(false);
  const [dropoffManual, setDropoffManual] = useState(false);
  const [inventorySearch, setInventorySearch] = useState('');
  const [debouncedInventorySearch, setDebouncedInventorySearch] = useState('');
  const [hasEligibleInventory, setHasEligibleInventory] = useState(false);
  const createTourStorageKey = isFoodRun
    ? CREATE_FOOD_RUN_TOUR_KEY
    : CREATE_DONATION_TOUR_KEY;
  const createTourId = isFoodRun
    ? 'food_runs_create_dialog'
    : 'donations_create_dialog';
  const activeCreateTourSteps = isFoodRun
    ? foodRunCreateTourSteps
    : createDonationTourSteps;

  // Tour functionality
  const { startTour, isRunning: isTourRunning } = useTourContext();
  const [hasShownTour, setHasShownTour] = useState(() => {
    return localStorage.getItem(createTourStorageKey) === 'true';
  });

  // Auto-start tour on first dialog open
  useEffect(() => {
    if (open && !hasShownTour) {
      const timer = setTimeout(() => {
        startTour(activeCreateTourSteps, createTourId);
        localStorage.setItem(createTourStorageKey, 'true');
        setHasShownTour(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [
    open,
    hasShownTour,
    startTour,
    activeCreateTourSteps,
    createTourId,
    createTourStorageKey,
  ]);

  const handleStartTour = () => {
    // Defer so the help-button click doesn't hit the tour overlay and
    // immediately close it (driver.js default overlayClickBehavior is close).
    window.setTimeout(() => {
      startTour(activeCreateTourSteps, createTourId);
    }, 50);
  };

  const foodTypes = useFoodTypesSelector();
  const vehicleTypes = useGetVehicleLabels();
  const daysOfTheWeek = useDaysOfTheWeek();
  const generateFoodTypesObj = useGenerateFoodTypesObj();
  const { defaultDistanceUnit, defaultWeightUnit } = useMeasureFieldUnits();
  const canUseInventory = open && !isFoodRun;
  const inventoryQuery = useInventoryItems({
    enabled: canUseInventory,
    status: 'active',
    search: debouncedInventorySearch,
    limit: 5,
    offset: 0,
    eligibleForDonation: true,
  });
  const availableInventoryItems = inventoryQuery.data?.items ?? [];

  useEffect(() => {
    if (canUseInventory && !debouncedInventorySearch && inventoryQuery.data) {
      setHasEligibleInventory(inventoryQuery.data.items?.length > 0);
    }
  }, [canUseInventory, debouncedInventorySearch, inventoryQuery.data]);

  useEffect(() => {
    const timeout = window.setTimeout(
      () => setDebouncedInventorySearch(inventorySearch.trim()),
      300
    );
    return () => window.clearTimeout(timeout);
  }, [inventorySearch]);

  const {
    donors,
    recipients,
    fetchRecipientMatches,
    invalidateMyOrgs,
    recipientMatchesLoading,
  } = useOrgsForFoodRunCreation({
    lat: user.lat,
    lng: user.lng,
    fetchOnMount: false,
  });
  const { getNearByNPOs, nearbyNPOs, nearbyNPOsLoading, hasLoadedNearbyNPOs } =
    useOrg({
      history: null,
      user,
      isNonProfit: false,
      fetchOnMount: false,
    });
  const { enqueueSnackbar } = useSnackbar();
  const titleInputRef = useRef(null);
  const descriptionInputRef = useRef(null);
  const pickupInstructionsInputRef = useRef(null);
  const dropoffInstructionsInputRef = useRef(null);
  const canCreateRecurring = useCheckAction('recurring-donation');

  // Food movers can't be attached to compliance profiles, so every food run
  // recipient is assignable (buildRecipientOptions marks them so).
  const availableRecipientOptions = isFoodRun ? recipientsList : nearbyNPOs;
  const hasAssignableRecipients = availableRecipientOptions.some(
    (option) => option.onboarding?.isAssignable !== false
  );
  const allowOpenRecipientSelection = isFoodRun
    ? !hasAssignableRecipients
    : hasLoadedNearbyNPOs && !nearbyNPOsLoading && !hasAssignableRecipients;

  const emitFetchFoodRuns = useEventBus('foodrun', 'fetch', null, null, {
    crossTabChannel: true,
  });
  const socket = SocketIO.getIO();

  const daysOfTheWeekValidator = useGetDaysOfTheWeekValidator();

  const createAddDonationSchema = useAddDonationSchema();
  const createAddFoodRunSchema = useAddFoodRunSchema();

  const multipleDriversSchema = useMultipleDriversSchema();
  const addFoodRunSchema = createAddFoodRunSchema(donorsList, recipientsList, {
    allowOpenRecipientSelection,
  })
    .concat(
      y.object({
        ...(ENABLE_MULTIPLE_DRIVERS
          ? {
              drivers: y
                .array()
                .when(['occurrenceType', 'startDate', 'endTime'], {
                  is: (occurrenceType, startDate, endTime) => {
                    let endTimeMoment = moment(endTime);
                    let startDateAndEndTime = moment(startDate)
                      .hours(endTimeMoment.hours())
                      .minutes(endTimeMoment.minutes());
                    return (
                      occurrenceType === 'onetime' &&
                      startDateAndEndTime.isBefore(moment())
                    );
                  },
                  then: () => multipleDriversSchema,
                  otherwise: () => y.array().nullable().notRequired(),
                }),
            }
          : {
              driverId: y
                .string()
                .when(['occurrenceType', 'startDate', 'endTime'], {
                  is: (occurrenceType, startDate, endTime) => {
                    let endTimeMoment = moment(endTime);
                    let startDateAndEndTime = moment(startDate)
                      .hours(endTimeMoment.hours())
                      .minutes(endTimeMoment.minutes());
                    return (
                      occurrenceType === 'onetime' &&
                      startDateAndEndTime.isBefore(moment())
                    );
                  },
                  then: () =>
                    y
                      .string()
                      .notOneOf(['unassigned'], t('donations.select-a-driver'))
                      .required(t('donations.select-a-driver')),
                  otherwise: () => y.string().optional(),
                }),
            }),
        receiverTeam: y.string().when('isPartialRescue', {
          is: true,
          then: () => y.string().optional(),
          otherwise: () =>
            allowOpenRecipientSelection
              ? y.string().optional()
              : y
                  .string()
                  .notOneOf(['unassigned'], t('donations.select-a-receiver'))
                  .required(t('donations.select-a-receiver')),
        }),
      })
    )
    .test({
      test: daysOfTheWeekValidator,
    });

  const isDriverRequiredForDonation = (data) => {
    return (
      data.deliveryOption === 0 &&
      (data.isPartialRescue
        ? Object.values(data.foodTypes).some((foodType) =>
            foodType?.assignedRecipients?.some(
              (r) => r.teamId && r.teamId !== 'unassigned'
            )
          )
        : data.receiverTeam && data.receiverTeam !== 'unassigned')
    );
  };

  const shouldShowDriverSelector = (data) => {
    if (isFoodRun) return true;

    return (
      data.deliveryOption === 0 &&
      (data.isPartialRescue
        ? Object.values(data.foodTypes).some((foodType) =>
            foodType?.assignedRecipients?.some(
              (r) => r.teamId && r.teamId !== 'unassigned'
            )
          )
        : data.receiverTeam && data.receiverTeam !== 'unassigned')
    );
  };
  const addDonationSchema = createAddDonationSchema(nearbyNPOs, {
    allowOpenRecipientSelection,
  }).concat(
    y.object({
      pickupLocation: y.object({}).when(['deliveryOption'], {
        is: (deliveryOption) => deliveryOption === 1,
        then: () =>
          y.object({}).required(t('donations.donation-location-required')),
        otherwise: () => y.object({}).optional(),
      }),
      ...(ENABLE_MULTIPLE_DRIVERS
        ? {
            drivers: y
              .array()
              .when(
                [
                  'deliveryOption',
                  'receiverTeam',
                  'isPartialRescue',
                  'foodTypes',
                ],
                {
                  is: (
                    deliveryOption,
                    receiverTeam,
                    isPartialRescue,
                    foodTypes
                  ) =>
                    isDriverRequiredForDonation({
                      deliveryOption,
                      receiverTeam,
                      isPartialRescue,
                      foodTypes,
                    }),
                  then: () => multipleDriversSchema,
                  otherwise: () => y.array().nullable().notRequired(),
                }
              ),
          }
        : {
            driverId: y
              .string()
              .when(
                [
                  'deliveryOption',
                  'receiverTeam',
                  'isPartialRescue',
                  'foodTypes',
                ],
                {
                  is: (
                    deliveryOption,
                    receiverTeam,
                    isPartialRescue,
                    foodTypes
                  ) =>
                    isDriverRequiredForDonation({
                      deliveryOption,
                      receiverTeam,
                      isPartialRescue,
                      foodTypes,
                    }),
                  then: () =>
                    y
                      .string()
                      .required(t('donations.select-a-driver'))
                      .notOneOf(['unassigned'], t('donations.select-a-driver')),
                  otherwise: () => y.string().optional(),
                }
              ),
          }),
    })
  );

  const schema = (isFoodRun ? addFoodRunSchema : addDonationSchema).shape({
    foodTypeNames: y
      .array()
      .of(y.string())
      .min(1, t('donations.at-least-one-food-type')),
    title: y
      .string()
      .transform((val) => (typeof val === 'string' ? val.trim() : val))
      .max(120, t('donations.title-max-error'))
      .required(
        isFoodRun
          ? t('donations.title-required_food-run')
          : t('donations.title-required_donation')
      ),
    description: y
      .string()
      .transform((val) => (typeof val === 'string' ? val.trim() : val))
      .max(500, t('donations.description-max-error'))
      .required(
        isFoodRun
          ? t('donations.description-required_food-run')
          : t('donations.description-required_donation')
      ),
  });

  const { vehicles = [], invalidate: fetchVehicles } = useVehicles({
    fetchOnMount: false,
  });
  const { activeMembers, invalidate: fetchTeamMembers } = useTeamMembers({
    fetchOnMount: false,
  });
  const { drivers, checkAvailabilityForDrivers } = useDriverAvailability();

  const [loading, setLoading] = useState(false);

  const useAIToFillDetailsFromLocalStorage = useMemo(() => {
    const aiPreference = localStorage?.getItem('aiTogglePreference');
    if (aiPreference === 'off') {
      return false;
    }
    return true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const hasStoredPreferenceOnOpen = useMemo(() => {
    const hasPreference = !!localStorage?.getItem('aiTogglePreference');
    return hasPreference;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const defaultValues = {
    useAIToFillDetails:
      !isCopy &&
      !isRepost &&
      useAIToFillDetailsFromLocalStorage &&
      user.ai_enabled,
    rememberAIChoice: false,
    donatingFromInventory: null,
    selectedInventoryItems: [],
    inventoryItems: [],
    inventoryDescriptionTruncated: false,
    inventoryPrefillSnapshot: null,
    images: [],
    foodTypes: {},
    foodTypeNames: [],
    filledFoodTypes: [],
    destinationAddresses: [],
    estimatedRescueTimeMinutes: user.rescue_time_tracking_enabled
      ? user.default_rescue_time_minutes > 0
        ? user.default_rescue_time_minutes
        : 30
      : 30,
    title: '',
    description: '',
    isPartialRescue: false,
    vehicleSize: 0,
    receiverTeam: 'unassigned',
    driverId: 'unassigned',
    drivers: [],
    vehicle: '',
    deliveryOption: 1,
    occurrenceType: 'onetime',
    startDate: new Date(
      moment().tz(user.timezone_id).format('YYYY-MM-DD HH:mm:ss')
    ),
    startTime: new Date(
      roundMinutes(moment()).tz(user.timezone_id).format('YYYY-MM-DD HH:mm:ss')
    ),
    endDate: new Date(
      moment().tz(user.timezone_id).add(1, 'days').format('YYYY-MM-DD HH:mm:ss')
    ),
    endTime: new Date(
      roundMinutes(moment())
        .tz(user.timezone_id)
        .add(24, 'hours')
        .format('YYYY-MM-DD HH:mm:ss')
    ),
    interval: 'Daily',
    dayOfTheWeek: [],
    dayOfTheMonth: {
      isLastDay: false,
      customDay: null,
      week: {
        number: null,
        dayOfTheWeek: null,
      },
    },
    deliveryRadius: user?.delivery_radius
      ? new Distance(user?.delivery_radius, 'mi').asRounded(
          defaultDistanceUnit.value
        )
      : null,
    pickupInstructions: '',
    dropoffInstructions: '',
    notifyOnEdit: false,
    additionalImages: [],
    availableOnUnassign: false,
    destLocation: {},
    pickupLocation: !isFoodRun
      ? {
          lat: user.lat,
          lng: user.lng,
          formattedAddress: user.formatted_address,
          name: user.place,
          streetAddress: user.street_address,
          city: user.city,
          province: user.province,
          country: user.country,
          zipCode: user.zip_code,
          placeId: user.place_id,
          timeZoneId: user.timezone_id,
        }
      : {},
    isFoodMover,
    useCustomPickupAddress: false,
    useCustomDestAddress: false,
  };

  // Build ordered list of steps based on feature flags & donation type
  const steps = useMemo(() => {
    const list = [];

    list.push(STEP.UPLOAD_IMAGE, STEP.TITLE, STEP.DESCRIPTION);

    // Delivery method selection
    list.push(STEP.DELIVERY_METHOD);

    // Partial rescue only for donations and when feature enabled
    if (!isFoodRun && ENABLE_CREATE_DONATION_PARTIAL) {
      list.push(STEP.PARTIAL_RESCUE);
    }

    // Food type / weight selection
    list.push(STEP.SELECT_FOOD_TYPES);

    // Time / recurring selection (always included)
    list.push(STEP.TIME_RECURRING);

    // Logistics (driver / vehicle etc.)
    list.push(STEP.LOGISTICS);

    // Instructions
    list.push(STEP.INSTRUCTIONS);

    // Summary / recap
    list.push(STEP.RECAP);

    return list;
  }, [isFoodRun]);

  const stepIndices = useMemo(() => {
    const obj = {};
    steps.forEach((s, idx) => {
      obj[s] = idx;
    });
    return obj;
  }, [steps]);

  const {
    watch,
    formState: { errors },
    control,
    setValue,
    getValues,
    clearErrors,
    trigger,
    getFieldState,
    reset,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues,
  });

  const onClose = () => {
    closeDialog();
    setStep(0);
    setInventorySearch('');
    setDebouncedInventorySearch('');
    setHasEligibleInventory(false);
    setHasInitialized(false);
    setHasReachedSummary(false);
    reset();
  };

  const scrollToTop = () => {
    setTimeout(() => {
      const dialogContent = document.querySelector('#dialog-create-donation');
      if (dialogContent) {
        dialogContent.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }, 100);
  };

  // The shortcut skips the per-step Next validation, so validate everything first.
  const goToSummary = async () => {
    const invalidStep = await findFirstInvalidStep({
      fieldsByStep: fieldsToValidateByStep,
      trigger,
      hasError: (field) => !!getFieldState(field).error,
    });
    const recapIndex = visibleSteps.indexOf(STEP.RECAP);
    setStep(
      invalidStep ?? (recapIndex === -1 ? visibleSteps.length - 1 : recapIndex)
    );
    scrollToTop();
  };

  const data = watch();
  const hasSelectedInventory = (data.selectedInventoryItems ?? []).length > 0;
  const shouldOfferInventory =
    canUseInventory &&
    (hasEligibleInventory ||
      hasSelectedInventory ||
      Boolean(inventoryQuery.isError));
  const selectedReceiverStatus =
    !isFoodRun && data.receiverTeam && data.receiverTeam !== 'unassigned'
      ? nearbyNPOs.find((team) => team.team_id === data.receiverTeam)
          ?.onboarding
      : null;
  // Warnings stay visible after a receiver is picked (on the field and Recap),
  // not only while the dropdown is open.
  const selectedRecipientWarnings = isFoodRun
    ? getRecipientMatchLabels(
        recipientsList.find(({ team_id }) => team_id === data.receiverTeam),
        t
      ).warnings
    : '';
  const selectedReceiverOrg =
    data.receiverTeam && data.receiverTeam !== 'unassigned'
      ? (isFoodRun ? recipientsList : nearbyNPOs).find(
          (team) => team.team_id === data.receiverTeam
        )
      : null;
  // Donating to an unverified recipient is allowed, so this is a heads-up on
  // the field, not a validation error.
  const taxExemptWarning =
    getTaxExemptStatus(selectedReceiverOrg) === TAX_EXEMPT_STATUS.UNVERIFIED
      ? `⚠ ${t('tax-exempt.unverified-helper')}`
      : '';
  const receiverHelperText =
    errors?.receiverTeam?.message ||
    (selectedReceiverStatus?.isPending
      ? allowOpenRecipientSelection
        ? t('donations.no-assignable-recipients-open-helper')
        : t('donations.recipient-pending-onboarding-helper')
      : !isFoodRun && (!hasLoadedNearbyNPOs || nearbyNPOsLoading)
        ? ''
        : allowOpenRecipientSelection
          ? t('donations.no-assignable-recipients-open-helper')
          : selectedRecipientWarnings
            ? `⚠ ${selectedRecipientWarnings}`
            : taxExemptWarning);
  const receiverHelperIsWarning =
    !errors?.receiverTeam?.message &&
    (receiverHelperText === `⚠ ${selectedRecipientWarnings}` ||
      (!!taxExemptWarning && receiverHelperText === taxExemptWarning));
  const receiverLabel = t(
    isFoodRun ||
      data.deliveryOption === 0 ||
      isDonationReceiverRequired({
        endDate: data.endDate,
        isPartialRescue: data.isPartialRescue,
        allowOpenRecipientSelection,
      })
      ? 'donations.assign-receiver'
      : 'donations.assign-receiver-optional'
  );

  const fieldsToValidateByStep = useMemo(
    () =>
      steps.map((s) => {
        const fields = validationByStepEnum[s] || [];
        if (isFoodRun && s === STEP.UPLOAD_IMAGE) {
          return fields.filter((f) => f !== 'images');
        }
        if (s === STEP.TIME_RECURRING && data.deliveryOption === 4) {
          return fields.filter((f) => f !== 'endDate' && f !== 'endTime');
        }
        if (!isFoodRun && s === STEP.LOGISTICS && data.deliveryOption === 4) {
          return fields.filter(
            (f) =>
              f !== 'driverId' &&
              f !== 'drivers' &&
              f !== 'receiverTeam' &&
              f !== 'donor' &&
              f !== 'deliveryRadius' &&
              f !== 'vehicleSize'
          );
        }
        return fields;
      }),
    [steps, isFoodRun, data.deliveryOption]
  );

  const visibleSteps = useMemo(
    () =>
      data.deliveryOption === 4
        ? steps.filter(
            (s) =>
              s !== STEP.INSTRUCTIONS &&
              s !== STEP.LOGISTICS &&
              s !== STEP.PARTIAL_RESCUE
          )
        : steps,
    [steps, data.deliveryOption]
  );

  const effectiveStepsLength = useMemo(
    () => visibleSteps.length,
    [visibleSteps]
  );

  // the logical STEP for the current numeric index
  const currentStep = visibleSteps[step];

  // readable check
  const isStep = (stepEnum) => currentStep === stepEnum;

  useEffect(() => {
    if (!open || loading || isWaitingForAnalysis) return;

    const focusRefByStep = {
      [STEP.TITLE]: titleInputRef,
      [STEP.DESCRIPTION]: descriptionInputRef,
    };
    const refToFocus =
      currentStep === STEP.INSTRUCTIONS
        ? isFoodRun || data.deliveryOption === 1
          ? pickupInstructionsInputRef
          : dropoffInstructionsInputRef
        : focusRefByStep[currentStep];

    if (!refToFocus?.current) return;

    const timer = setTimeout(() => {
      const input = refToFocus.current;
      if (!input) return;

      input.focus({ preventScroll: true });

      const valueLength =
        typeof input.value === 'string' ? input.value.length : 0;
      input.setSelectionRange?.(valueLength, valueLength);
    }, 150);

    return () => clearTimeout(timer);
  }, [
    open,
    currentStep,
    loading,
    isWaitingForAnalysis,
    isFoodRun,
    data.deliveryOption,
  ]);

  const criticalSections = [
    STEP.PARTIAL_RESCUE,
    STEP.TIME_RECURRING,
    STEP.LOGISTICS,
  ];

  const isCurrentStepCritical = criticalSections.includes(currentStep);

  const shouldShowSummaryButton =
    hasReachedSummary && !isCurrentStepCritical && !isStep(STEP.RECAP);

  // Auto-bump endDate for one-time donations when endTime crosses midnight
  useEffect(() => {
    if (data.occurrenceType !== 'onetime' || !data.startDate) return;

    const userTimeZone = user.timezone_id || 'America/Los_Angeles';
    const baseDateTz = moment.tz(data.startDate, userTimeZone);

    const startT = new Date(data.startTime);
    const endT = new Date(data.endTime);

    const startMoment = moment.tz(
      {
        year: baseDateTz.year(),
        month: baseDateTz.month(),
        day: baseDateTz.date(),
        hour: startT.getHours(),
        minute: startT.getMinutes(),
        second: startT.getSeconds(),
      },
      userTimeZone
    );

    const endMomentSameDay = moment.tz(
      {
        year: baseDateTz.year(),
        month: baseDateTz.month(),
        day: baseDateTz.date(),
        hour: endT.getHours(),
        minute: endT.getMinutes(),
        second: endT.getSeconds(),
      },
      userTimeZone
    );

    if (endMomentSameDay.isBefore(startMoment)) {
      const expectedEndDate = baseDateTz.clone().add(1, 'day');
      if (!moment(data.endDate).isSame(expectedEndDate, 'day')) {
        setValue(
          'endDate',
          new Date(expectedEndDate.format('YYYY-MM-DD HH:mm:ss'))
        );
      }
    }
  }, [
    data.occurrenceType,
    data.startDate,
    data.startTime,
    data.endTime,
    data.endDate,
    setValue,
    user.timezone_id,
  ]);

  const checkIfLastStep = (stepIdx) => stepIdx === visibleSteps.length - 1;

  const analysisContext = useMemo(() => {
    return {
      donorName: user?.business_name || user?.name,
      organizationType: user?.is_non_profit ? 'non-profit' : 'business',
    };
  }, [user?.business_name, user?.name, user?.is_non_profit]);

  const handleAnalysisComplete = (analysisResults) => {
    setIsWaitingForAnalysis(false);
    const previousAttempts = Array.isArray(data.aiAnalysisResult?.attempts)
      ? data.aiAnalysisResult.attempts
      : [];

    if (analysisResults.error) {
      setAnalysisError({
        message: analysisResults.error,
        failureType: analysisResults.failureType,
      });
      setValue('aiAnalysisResult', {
        ...analysisResults,
        attempts: [...previousAttempts, analysisResults],
      });
      return;
    }

    setAnalysisError(null);

    const selectedInventoryItems = getValues('selectedInventoryItems') ?? [];
    const currentSnapshot = getValues('inventoryPrefillSnapshot');
    const canReplaceFoodTypes =
      !currentSnapshot ||
      (_.isEqual(getValues('foodTypeNames'), currentSnapshot.categories) &&
        _.isEqual(getValues('foodTypes'), currentSnapshot.foodTypes));
    const canReplaceTitle =
      !(getValues('title') || '').trim() ||
      getValues('title') === currentSnapshot?.title;
    const canReplaceDescription =
      !(getValues('description') || '').trim() ||
      getValues('description') === currentSnapshot?.description;
    const validFoodTypeIds = foodTypes.map((item) => item.id);
    const mergedPrefill = mergeInventoryDonationAnalysis(
      selectedInventoryItems,
      analysisResults,
      {
        weightUnit: defaultWeightUnit.value,
        validFoodTypes: validFoodTypeIds,
        minAnalysisWeightLbs: 0.2,
      }
    );
    const inventoryCategories =
      mergedPrefill.inventoryPrefill.categories.filter((category) =>
        validFoodTypeIds.includes(category)
      );
    const inventoryWeights = Object.fromEntries(
      Object.entries(mergedPrefill.inventoryPrefill.weights).filter(
        ([category]) => validFoodTypeIds.includes(category)
      )
    );
    const inventoryFoodTypes = generateFoodTypesObj({
      foodTypeNames: inventoryCategories,
      existingFoodTypes: inventoryWeights,
    });
    const combinedFoodTypes = generateFoodTypesObj({
      foodTypeNames: mergedPrefill.categories,
      existingFoodTypes: mergedPrefill.weights,
    });

    if (
      !_.isEqual(selectedInventoryItems, mergedPrefill.selectedInventoryItems)
    ) {
      setValue('selectedInventoryItems', mergedPrefill.selectedInventoryItems);
    }
    setValue('inventoryItems', mergedPrefill.inventoryPrefill.inventoryItems);
    if (currentSnapshot) {
      setValue('inventoryPrefillSnapshot', {
        ...currentSnapshot,
        categories: inventoryCategories,
        foodTypes: inventoryFoodTypes,
        title: mergedPrefill.inventoryPrefill.title,
        description: mergedPrefill.inventoryPrefill.description,
        descriptionTruncated:
          mergedPrefill.inventoryPrefill.descriptionTruncated,
        inventoryItems: mergedPrefill.inventoryPrefill.inventoryItems,
      });
    }

    if (canReplaceFoodTypes) {
      setValue('foodTypes', combinedFoodTypes);
      setValue('foodTypeNames', mergedPrefill.categories);
    }
    setValue('filledFoodTypes', mergedPrefill.analysisCategories);
    setValue('aiAnalysisCategories', mergedPrefill.analysisCategories);
    setValue('aiAnalysisResult', {
      ...analysisResults,
      ...(previousAttempts.length > 0 ? { attempts: previousAttempts } : {}),
    });

    if (canReplaceTitle && mergedPrefill.title) {
      setValue('title', mergedPrefill.title);
    }
    if (canReplaceDescription && mergedPrefill.description) {
      setValue('description', mergedPrefill.description);
      setValue(
        'inventoryDescriptionTruncated',
        mergedPrefill.descriptionTruncated
      );
    }

    scrollToTop();
    setStep((prv) => prv + 1);
  };

  const clearAIAnalysisPrefill = () => {
    const analysis = data.aiAnalysisResult;
    const snapshot = data.inventoryPrefillSnapshot;
    const mergedPrefill = mergeInventoryDonationAnalysis(
      data.selectedInventoryItems,
      analysis,
      {
        weightUnit: defaultWeightUnit.value,
        validFoodTypes: foodTypes.map((item) => item.id),
        minAnalysisWeightLbs: 0.2,
      }
    );
    const combinedFoodTypes = generateFoodTypesObj({
      foodTypeNames: mergedPrefill.categories,
      existingFoodTypes: mergedPrefill.weights,
    });

    if (
      _.isEqual(data.foodTypeNames, mergedPrefill.categories) &&
      _.isEqual(data.foodTypes, combinedFoodTypes)
    ) {
      setValue('foodTypeNames', snapshot?.categories ?? []);
      setValue('foodTypes', snapshot?.foodTypes ?? {});
    }
    setValue('filledFoodTypes', []);
    setValue('aiAnalysisCategories', []);
    if (data.title === mergedPrefill.title) {
      setValue('title', snapshot?.title ?? '');
    }
    if (data.description === mergedPrefill.description) {
      setValue('description', snapshot?.description ?? '');
      setValue(
        'inventoryDescriptionTruncated',
        snapshot?.descriptionTruncated ?? false
      );
    }
    const previousAttempts = analysis?.attempts;
    setValue(
      'aiAnalysisResult',
      Array.isArray(previousAttempts) && previousAttempts.length > 0
        ? { attempts: previousAttempts }
        : null
    );
  };

  useEffect(() => {
    setIsWaitingForAnalysis(false);
    setAnalysisError(null);
  }, [open, step]);

  // Clear the AI analysis whenever the photos change
  useEffect(() => {
    clearAIAnalysisPrefill();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.images]);

  const { analyzeFoodImages } = useFoodAnalysis();
  const handleAnalyzeFoodImages = async (files, inventoryItems = []) => {
    try {
      setIsWaitingForAnalysis(true);

      const filesToAnalyze = files;
      const actualFiles = filesToAnalyze.map((file) => {
        const isUrl = typeof file === 'string';
        return isUrl ? file : file.file || file;
      });

      const result = await analyzeFoodImages(actualFiles, {
        ...analysisContext,
        inventoryItems,
      });

      if (
        result?.success &&
        result?.data &&
        (result.data.categories?.length > 0 ||
          result.data.inventoryItems?.length > 0 ||
          inventoryItems.length > 0)
      ) {
        handleAnalysisComplete(result.data);
      } else if (result?.success && result?.data) {
        handleAnalysisComplete({
          ...result.data,
          success: false,
          failureType: 'no_items_detected',
          error: 'No food detected in images',
        });
      } else if (result?.error) {
        handleAnalysisComplete({
          success: false,
          failureType: result.timedOut ? 'timeout' : 'analysis_error',
          error: result.error,
          details: result.details || null,
          provider: result.provider || null,
          providerResponse: result.providerResponse || null,
          analyzedAt: result.analyzedAt || new Date().toISOString(),
        });
      } else {
        handleAnalysisComplete({
          success: false,
          failureType: 'empty_response',
          error: 'No food detected in images',
          analyzedAt: new Date().toISOString(),
        });
      }
    } catch (error) {
      handleAnalysisComplete({
        success: false,
        failureType: 'request_error',
        error: error.message || 'Analysis failed',
        analyzedAt: new Date().toISOString(),
      });
    } finally {
      setIsWaitingForAnalysis(false);
    }
  };

  const normalizeDefaultPickupLimits = () => {
    if (!data.isPartialRescue || !data.foodTypes) return;

    let changed = false;
    const nextFoodTypes = Object.entries(data.foodTypes).reduce(
      (acc, [foodTypeId, foodType]) => {
        const totalAmount = parseFloat(foodType?.value) || 0;
        const currentMaxAmount = parseFloat(foodType?.maxReservableWeight) || 0;
        const currentMinAmount = parseFloat(foodType?.minReservableWeight) || 0;
        const maxWasManuallyChanged = Boolean(
          foodType?.maxReservableWeightManuallyChanged
        );
        const minWasManuallyChanged = Boolean(
          foodType?.minReservableWeightManuallyChanged
        );

        const nextMin = minWasManuallyChanged ? currentMinAmount : 1;
        const nextMax = maxWasManuallyChanged ? currentMaxAmount : totalAmount;

        if (
          totalAmount > 0 &&
          (currentMinAmount !== nextMin || currentMaxAmount !== nextMax)
        ) {
          changed = true;
          acc[foodTypeId] = {
            ...foodType,
            minReservableWeight: nextMin,
            maxReservableWeight: nextMax,
          };
          return acc;
        }

        acc[foodTypeId] = foodType;
        return acc;
      },
      {}
    );

    if (changed) {
      setValue('foodTypes', nextFoodTypes, { shouldValidate: false });
    }
  };

  const clearInventoryPrefill = () => {
    const snapshot = data.inventoryPrefillSnapshot;
    let nextImages = data.images ?? [];
    if (snapshot) {
      if (
        _.isEqual(data.foodTypeNames, snapshot.categories) &&
        _.isEqual(data.foodTypes, snapshot.foodTypes)
      ) {
        setValue('foodTypeNames', []);
        setValue('foodTypes', {});
      }
      if (data.description === snapshot.description) {
        setValue('description', '');
      }
      if (data.title === snapshot.title) {
        setValue('title', '');
      }
      nextImages = (data.images ?? []).filter(
        (image) =>
          !snapshot.images.some((source) =>
            matchesStoredImage(image, source.url)
          )
      );
      if (!_.isEqual(data.images, nextImages)) {
        setValue('images', nextImages);
      }
    }

    setValue('selectedInventoryItems', []);
    setValue('inventoryItems', []);
    setValue('inventoryDescriptionTruncated', false);
    setValue('inventoryPrefillSnapshot', null);
    return nextImages;
  };

  const applyInventorySelection = () => {
    const selectedInventoryItems = data.selectedInventoryItems ?? [];
    const hasSelectedInventory = selectedInventoryItems.length > 0;
    setValue('donatingFromInventory', hasSelectedInventory);

    if (!hasSelectedInventory) {
      return {
        selectedInventoryItems: [],
        userImages: clearInventoryPrefill(),
      };
    }

    const prefill = buildInventoryDonationPrefill(selectedInventoryItems, {
      weightUnit: defaultWeightUnit.value,
    });
    const validFoodTypeIds = new Set(foodTypes.map((item) => item.id));
    const categories = prefill.categories.filter((category) =>
      validFoodTypeIds.has(category)
    );
    const weights = Object.fromEntries(
      Object.entries(prefill.weights).filter(([category]) =>
        validFoodTypeIds.has(category)
      )
    );
    const nextFoodTypes = generateFoodTypesObj({
      foodTypeNames: categories,
      existingFoodTypes: weights,
    });
    const previousPrefill = data.inventoryPrefillSnapshot;
    const foodPrefillIsUnchanged =
      !previousPrefill ||
      (_.isEqual(data.foodTypeNames, previousPrefill.categories) &&
        _.isEqual(data.foodTypes, previousPrefill.foodTypes));
    const descriptionPrefillIsUnchanged =
      !previousPrefill || data.description === previousPrefill.description;
    const titlePrefillIsUnchanged =
      !previousPrefill || data.title === previousPrefill.title;
    const userImages = previousPrefill
      ? (data.images ?? []).filter(
          (image) =>
            !previousPrefill.images.some((source) =>
              matchesStoredImage(image, source.url)
            )
        )
      : (data.images ?? []);
    const currentlyAttachedInventoryImages = prefill.images.filter((image) =>
      (data.images ?? []).some((currentImage) =>
        matchesStoredImage(currentImage, image.url)
      )
    );
    const retainedInventoryImages = retainInventoryPhotosForDonation(
      prefill.images,
      previousPrefill?.images,
      currentlyAttachedInventoryImages
    );
    const inventoryImages = selectInventoryPhotosForDonation(
      retainedInventoryImages,
      userImages,
      { maxImages: 6 }
    );
    const nextImages = [
      ...inventoryImages.map((image) => image.url),
      ...userImages,
    ];

    if (foodPrefillIsUnchanged) {
      setValue('foodTypeNames', categories);
      setValue('foodTypes', nextFoodTypes);
    }
    if (descriptionPrefillIsUnchanged) {
      setValue('description', prefill.description);
    }
    if (titlePrefillIsUnchanged) {
      setValue('title', prefill.title);
    }
    if (!_.isEqual(data.images, nextImages)) {
      setValue('images', nextImages);
    }
    setValue('inventoryItems', prefill.inventoryItems);
    setValue(
      'inventoryDescriptionTruncated',
      descriptionPrefillIsUnchanged && prefill.descriptionTruncated
    );
    setValue('inventoryPrefillSnapshot', {
      categories,
      foodTypes: nextFoodTypes,
      title: prefill.title,
      description: prefill.description,
      descriptionTruncated: prefill.descriptionTruncated,
      images: inventoryImages,
      inventoryItems: prefill.inventoryItems,
    });

    return { selectedInventoryItems, userImages };
  };

  const onNextStep = async () => {
    let analysisImages = data.images ?? [];
    let inventoryAnalysisItems = [];
    let selectedInventoryForStep = data.selectedInventoryItems ?? [];
    if (
      isStep(STEP.UPLOAD_IMAGE) &&
      (canUseInventory || data.inventoryPrefillSnapshot)
    ) {
      const inventorySelection = applyInventorySelection();
      analysisImages = inventorySelection.userImages;
      inventoryAnalysisItems = buildInventoryAnalysisItems(
        inventorySelection.selectedInventoryItems
      );
      selectedInventoryForStep = inventorySelection.selectedInventoryItems;
    }

    if (isStep(STEP.SELECT_FOOD_TYPES)) {
      normalizeDefaultPickupLimits();
    }

    if (!(await trigger(fieldsToValidateByStep[step]))) {
      // Scroll to first visible error so the user can see what's blocking
      setTimeout(() => {
        const dialogContent = document.querySelector('#dialog-create-donation');
        const firstError = dialogContent?.querySelector(
          '.Mui-error, [class*="error"]'
        );
        if (firstError) {
          firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
      return;
    }

    if (isStep(STEP.UPLOAD_IMAGE) && analysisImages.length > 0) {
      const indices = [];
      for (let i = 0; i < analysisImages.length; i += 1) {
        const img = analysisImages[i];
        if (typeof img === 'string') continue;
        const file = img?.file || img;
        if (!(file instanceof Blob)) continue;
        // eslint-disable-next-line no-await-in-loop
        const result = await validateImageDecodable(file);
        if (!result.ok) indices.push(i);
      }
      if (indices.length > 0) {
        setCorruptedImageIndices(indices);
        return;
      }
      setCorruptedImageIndices([]);
    }

    if (
      isStep(STEP.UPLOAD_IMAGE) &&
      data.useAIToFillDetails &&
      selectedInventoryForStep.length === 0 &&
      analysisImages.length > 0 &&
      !data.aiAnalysisResult?.categories?.length &&
      !isCopy &&
      !isRepost
    ) {
      setIsWaitingForAnalysis(true);
      handleAnalyzeFoodImages(analysisImages, inventoryAnalysisItems);
      setAnalysisError(null);

      return;
    }

    scrollToTop();

    const isLastStep = checkIfLastStep(step);

    if (isLastStep) {
      setLoading(true);
      try {
        let pickupTimeZoneId = null;
        if (_isNumber(data?.pickupLocation?.lat)) {
          pickupTimeZoneId = await getTimeZoneIdFromCoords(
            data?.pickupLocation
          );
        }
        let destTimeZoneId = null;
        if (_isNumber(data?.destLocation?.lat)) {
          destTimeZoneId = await getTimeZoneIdFromCoords(data?.destLocation);
        }

        if (!isFoodRun && data.deliveryOption === 4) {
          setValue('endDate', data.startDate);
          setValue('endTime', data.startTime);
        }

        if (data.deliveryOption === 1 && !isFoodRun) {
          if (ENABLE_MULTIPLE_DRIVERS) {
            data.drivers = [];
          } else {
            delete data.driverId;
          }
        }

        const donationId = uuidv4();
        const patternId = uuidv4();
        const isRecurring = ['recurring', 'indefinite'].includes(
          data.occurrenceType
        );

        const s3Folder = isRecurring
          ? `recurring-pattern/${patternId}`
          : `donation/${donationId}`;

        const stockImageUrls = new Set(
          (data.inventoryPrefillSnapshot?.images ?? []).map(({ url }) => url)
        );
        const mainImages = data.inventoryItems?.length
          ? await Promise.all(
              (data.images ?? []).map((image) =>
                typeof image === 'string'
                  ? backend.image.copy({
                      folder: s3Folder,
                      url: image,
                      // Inventory photos are marked as stock copies (#609).
                      stock: stockImageUrls.has(image),
                    })
                  : image
              )
            )
          : (data.images ?? []);
        const images = await uploadImages({
          images: [...mainImages, ...(data.additionalImages ?? [])],
          folder: s3Folder,
          source: isFoodRun ? 'Create Food Run' : 'Create Donation',
        });
        const foodCount = (data.images ?? []).length;
        const additionalCount = (data.additionalImages ?? []).length;
        const foodKeys = images.slice(0, foodCount);
        const additionalKeys = images.slice(
          foodCount,
          foodCount + additionalCount
        );

        if (isFoodRun) {
          const newFoodRun = processFoodRunFormData({
            ...data,
            available_on_unassign: data.availableOnUnassign,
            pickup_address: {
              ...data.pickupLocation,
              timeZoneId: pickupTimeZoneId,
            },
            dest_address: {
              ...data.destLocation,
              timeZoneId: destTimeZoneId,
            },
            timezone_id: pickupTimeZoneId ?? user.timezone_id,
            receiver: data.receiverTeam,
            driver: data.driverId,
            drivers: data.drivers,
            donationId,
            patternId,
          });
          newFoodRun.images = foodKeys;
          newFoodRun.additionalImages = additionalKeys;
          newFoodRun.aiEnabled = !!data.useAIToFillDetails;
          newFoodRun.aiAnalysisResult = data.aiAnalysisResult || null;

          await backend.foodRun.create(newFoodRun);
          emitFetchFoodRuns();
          invalidateUserData();
          socket.emit('DONATION-UPDATE', {
            patternId,
            donationId: null,
            teamId: user.team_id,
            type: 'FOOD-RUN',
          });
          history.push(PATH_DASHBOARD.foodRun.myRuns);
        } else {
          const newDonation = processDonationData({
            donationId,
            patternId,
            defaultDistanceUnit,
            user,
            isCopy,
            donationTimeZoneId: pickupTimeZoneId,
            destTimeZoneId,
            ...{
              ...data,
              ...(data.deliveryOption === 4
                ? { endDate: data.startDate, endTime: data.startTime }
                : {}),
            },
            images: foodKeys,
            additionalImages: additionalKeys,
          });

          const createNewDonation = async () => {
            await backend.donations.createRecurring(newDonation);
            // Let nearby teams refresh their available lists.
            socket.emit('DONATION-UPDATE', {
              patternId,
              donationId,
              teamId: user.team_id,
              type: 'DONATION',
            });

            if (!isRepost) {
              try {
                const progress = await tourAPI.getProgress(
                  POST_DONATION_TOUR_KEY
                );
                if (!progress?.completed && !progress?.skipped) {
                  await tourAPI.updateProgress(POST_DONATION_TOUR_KEY, {
                    completed: false,
                    skipped: false,
                    stepReached: POST_DONATION_TOUR_PENDING_STEP,
                  });
                }
              } catch {
                // Tour trigger should not block donation creation flow.
              }
            }

            history.push(PATH_DASHBOARD.donations);
            await invalidate?.();
          };

          if (isRepost) {
            try {
              await backend.donations.cancelDonation({
                donationId: initialDonation.id,
                teamId: user.team_id,
              });
            } catch {
              enqueueSnackbar(t('donations.repost-error'), {
                variant: 'error',
              });
              return;
            }
          }

          await createNewDonation();
        }

        onClose();
      } catch (err) {
        const errorMessage =
          (err && err.message) ||
          (err != null ? String(err) : '') ||
          t('donations.unexpected-error');
        enqueueSnackbar(errorMessage, { variant: 'error' });
      } finally {
        setLoading(false);
      }
      return;
    }

    const nextStep = step + 1;
    setStep(nextStep);

    if (nextStep === effectiveStepsLength - 1) {
      setHasReachedSummary(true);
    }
  };

  const handleFormSubmit = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (loading || isWaitingForAnalysis) return;

    await onNextStep();
  };

  const shouldAdvanceOnEnter = (event) => {
    if (
      event.key !== 'Enter' ||
      event.shiftKey ||
      event.metaKey ||
      event.ctrlKey ||
      event.altKey ||
      event.nativeEvent?.isComposing
    ) {
      return false;
    }

    const target = event.target;
    const tagName = target?.tagName;

    if (
      !target ||
      tagName === 'TEXTAREA' ||
      target.isContentEditable ||
      target.classList?.contains('pac-target-input') ||
      target.closest?.('button, a')
    ) {
      return false;
    }

    if (target.getAttribute?.('role') === 'combobox') {
      return false;
    }

    if (tagName !== 'INPUT') {
      return false;
    }

    const inputType = (target.getAttribute('type') || 'text').toLowerCase();
    return ![
      'button',
      'checkbox',
      'file',
      'hidden',
      'image',
      'radio',
      'range',
      'reset',
      'submit',
    ].includes(inputType);
  };

  const handleFormKeyDown = async (event) => {
    if (!shouldAdvanceOnEnter(event)) return;

    event.preventDefault();
    event.stopPropagation();

    if (loading || isWaitingForAnalysis) return;

    await onNextStep();
  };

  // Check driver availability when both pickup and end times are available
  useEffect(() => {
    if (
      data.startDate &&
      data.startTime &&
      data.endTime &&
      activeMembers.length &&
      user?.team_id
    ) {
      const userTimeZone = user.timezone_id || 'America/Los_Angeles';
      const date = moment(data.startDate);

      // If data.startTime is a Date, extract hours/minutes/seconds
      let hours = 0,
        minutes = 0,
        seconds = 0;
      if (data.startTime) {
        const t = new Date(data.startTime);
        hours = t.getHours();
        minutes = t.getMinutes();
        seconds = t.getSeconds();
      }

      // Combine date and time in the user's time zone
      const pickupMoment = moment.tz(
        {
          year: date.year(),
          month: date.month(),
          day: date.date(),
          hour: hours,
          minute: minutes,
          second: seconds,
        },
        userTimeZone
      );
      const pickupTimeISO = pickupMoment.toISOString();

      const tEnd = new Date(data.endTime);
      const endMoment = moment.tz(
        {
          year: date.year(),
          month: date.month(),
          day: date.date(),
          hour: tEnd.getHours(),
          minute: tEnd.getMinutes(),
          second: tEnd.getSeconds(),
        },
        userTimeZone
      );
      const endTimeISO = endMoment.toISOString();

      checkAvailabilityForDrivers({
        pickupTime: pickupTimeISO,
        endTime: endTimeISO,
        occurrenceType: data.occurrenceType,
        interval: data.interval,
        daysOfTheWeek: data.dayOfTheWeek,
        dayOfTheMonth: data.dayOfTheMonth,
      });
    }
  }, [
    data.startDate,
    data.startTime,
    data.endDate,
    data.endTime,
    data.occurrenceType,
    data.interval,
    data.dayOfTheWeek,
    data.dayOfTheMonth,
    activeMembers,
    checkAvailabilityForDrivers,
    user?.team_id,
    user.timezone_id,
  ]);

  useEffect(() => {
    setDonorsList(donors);
  }, [donors]);

  useEffect(() => {
    setRecipientsList(recipients);
  }, [recipients]);

  useEffect(() => {
    if (data.receiverTeam && data.receiverTeam !== 'unassigned') {
      let selectedTeam;
      if (isFoodRun) {
        selectedTeam = recipientsList.find(
          ({ team_id }) => team_id === data.receiverTeam
        );
      } else {
        selectedTeam =
          nearbyNPOs.find(({ team_id }) => team_id === data.receiverTeam) ?? {};
      }
      if (!selectedTeam) return;
      if (data.useCustomDestAddress) return;
      setValue('destLocation', {
        lat: selectedTeam.lat,
        lng: selectedTeam.lng,
        formattedAddress: selectedTeam.formatted_address,
        name: selectedTeam.place,
        streetAddress: selectedTeam.street_address,
        city: selectedTeam.city,
        province: selectedTeam.province,
        country: selectedTeam.country,
        zipCode: selectedTeam.zip_code,
        placeId: selectedTeam.place_id,
        timeZoneId: selectedTeam.timezone_id,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    data.receiverTeam,
    data.useCustomDestAddress,
    nearbyNPOs,
    recipientsList,
    isFoodRun,
  ]);

  const refreshNearbyNPOs = () => {
    getNearByNPOs({
      lat: user.lat,
      lng: user.lng,
      includeSelf: false,
      includeOnboarding: true,
      isDonationCreation: true,
    });
  };

  const refreshDonors = async () => {
    try {
      const myOrgs = await backend.team.getMyOrgs('DONOR', {
        ignoreTeamId: user.team_id,
      });
      setDonorsList(myOrgs);
    } catch (error) {
      console.error('Error refreshing donors', error);
    }
  };

  const recipientMatchRequest = useMemo(() => {
    if (!isFoodRun || !data.donor) return null;
    const selectedDonor = donors.find((team) => team.team_id === data.donor);
    if (!selectedDonor) return null;

    const request = buildFoodRunRecipientMatchRequest({
      donorTeamId: data.donor,
      pickupLocation: data.useCustomPickupAddress
        ? data.pickupLocation
        : selectedDonor,
      foodTypes: data.foodTypes,
      occurrenceType: data.occurrenceType,
      interval: data.interval,
      dayOfTheWeek: data.dayOfTheWeek,
      dayOfTheMonth: data.dayOfTheMonth,
      startDate: data.startDate,
      startTime: data.startTime,
      endTime: data.endTime,
    });
    return request.lat === null || request.lng === null ? null : request;
  }, [
    data.dayOfTheMonth,
    data.dayOfTheWeek,
    data.donor,
    data.endTime,
    data.foodTypes,
    data.interval,
    data.occurrenceType,
    data.pickupLocation,
    data.startDate,
    data.startTime,
    data.useCustomPickupAddress,
    donors,
    isFoodRun,
  ]);
  const recipientMatchRequestKey = JSON.stringify(recipientMatchRequest);
  const lastMatchedRequestKey = useRef(null);

  const refreshRecipients = async () => {
    try {
      await invalidateMyOrgs(data.donor);
      if (recipientMatchRequest) {
        lastMatchedRequestKey.current = recipientMatchRequestKey;
        await fetchRecipientMatches(recipientMatchRequest);
      }
    } catch (error) {
      console.error('Error refreshing recipients', error);
    }
  };

  useEffect(() => {
    if (data.donor && isFoodRun) {
      const selectedDonor = donors.find((team) => team.team_id === data.donor);
      if (selectedDonor) {
        invalidateMyOrgs(selectedDonor.team_id);
      }
      if (_isNumber(selectedDonor?.lat) && !data.useCustomPickupAddress) {
        setValue('pickupLocation', {
          lat: selectedDonor.lat,
          lng: selectedDonor.lng,
          formattedAddress: selectedDonor.formatted_address,
          name: selectedDonor.place,
          streetAddress: selectedDonor.street_address,
          city: selectedDonor.city,
          province: selectedDonor.province,
          country: selectedDonor.country,
          zipCode: selectedDonor.zip_code,
          placeId: selectedDonor.place_id,
          timeZoneId: selectedDonor.timezone_id,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.donor, data.useCustomPickupAddress, isFoodRun]);

  // Match only while the receiver picker (or the recap that names the
  // receiver) is on screen, so editing weights on earlier steps doesn't fire a
  // request per keystroke. Unchanged criteria reuse the last ranking.
  const receiverStepVisible =
    currentStep === STEP.LOGISTICS || currentStep === STEP.RECAP;
  useEffect(() => {
    if (
      !open ||
      !receiverStepVisible ||
      !recipientMatchRequest ||
      lastMatchedRequestKey.current === recipientMatchRequestKey
    ) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      lastMatchedRequestKey.current = recipientMatchRequestKey;
      fetchRecipientMatches(recipientMatchRequest).catch((error) => {
        lastMatchedRequestKey.current = null;
        console.error('Error matching food run recipients', error);
      });
    }, 300);

    return () => window.clearTimeout(timer);
  }, [
    fetchRecipientMatches,
    open,
    receiverStepVisible,
    recipientMatchRequest,
    recipientMatchRequestKey,
  ]);

  useEffect(() => {
    if (!open) lastMatchedRequestKey.current = null;
  }, [open]);

  useEffect(() => {
    if (open) {
      fetchVehicles();
      fetchTeamMembers();
      if (isFoodRun) {
        invalidateMyOrgs();
      } else {
        getNearByNPOs({
          lat: user.lat,
          lng: user.lng,
          includeSelf: false,
          includeOnboarding: true,
          isDonationCreation: true,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, user.lat, user.lng, isFoodRun]);

  useEffect(() => {
    if (open && initialDonation) {
      if (isFoodRun) {
        const initialData = parseFoodRunForCopy(
          initialDonation,
          user,
          vehicleTypes,
          patternData
        );

        reset({
          ...defaultValues,
          ...initialData,
          foodTypeNames: Object.keys(initialData.foodTypes),
          receiverTeam: initialData.receiver,
          pickupLocation: initialData.pickup_address,
          destLocation: initialData.dest_address,
        });

        if ((initialData.pickupInstructions || '').trim().length > 0) {
          setPickupManual(true);
        }
        if ((initialData.dropoffInstructions || '').trim().length > 0) {
          setDropoffManual(true);
        }
      } else {
        const initialData = parseDonation(
          initialDonation,
          user,
          isRepost,
          isCopy,
          patternData,
          partialRescues
        );
        reset({
          ...defaultValues,
          ...initialData,
          foodTypeNames: Object.keys(initialData.foodTypes),
          pickupLocation: initialData.location,
          vehicleSize: {
            CAR: 0,
            VAN: 1,
            TRUCK: 2,
          }[initialDonation.vehicle],
        });

        if ((initialData.pickupInstructions || '').trim().length > 0) {
          setPickupManual(true);
        }
        if ((initialData.dropoffInstructions || '').trim().length > 0) {
          setDropoffManual(true);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, JSON.stringify(initialDonation)]);

  useEffect(() => {
    if (open && !initialDonation) {
      const aiPreference = localStorage?.getItem('aiTogglePreference');
      const shouldUseAI = aiPreference !== 'off';

      // Create completely fresh default values to avoid any stale references
      const freshDefaultValues = {
        useAIToFillDetails:
          !isCopy && !isRepost && shouldUseAI && !!user.ai_enabled,
        rememberAIChoice: false,
        donatingFromInventory: null,
        selectedInventoryItems: [],
        inventoryItems: [],
        inventoryDescriptionTruncated: false,
        inventoryPrefillSnapshot: null,
        images: [],
        foodTypes: {},
        foodTypeNames: [],
        filledFoodTypes: [],
        destinationAddresses: [],
        estimatedRescueTimeMinutes: user.rescue_time_tracking_enabled
          ? user.default_rescue_time_minutes > 0
            ? user.default_rescue_time_minutes
            : 30
          : 30,
        title: '',
        description: '',
        isPartialRescue: false,
        vehicleSize: 0,
        receiverTeam: 'unassigned',
        driverId: 'unassigned',
        drivers: [],
        vehicle: '',
        deliveryOption: 1,
        occurrenceType: 'onetime',
        startDate: new Date(
          moment().tz(user.timezone_id).format('YYYY-MM-DD HH:mm:ss')
        ),
        startTime: new Date(
          roundMinutes(moment())
            .tz(user.timezone_id)
            .format('YYYY-MM-DD HH:mm:ss')
        ),
        endDate: new Date(
          moment()
            .tz(user.timezone_id)
            .add(1, 'days')
            .format('YYYY-MM-DD HH:mm:ss')
        ),
        endTime: new Date(
          roundMinutes(moment())
            .tz(user.timezone_id)
            .add(24, 'hours')
            .format('YYYY-MM-DD HH:mm:ss')
        ),
        interval: 'Daily',
        dayOfTheWeek: [],
        dayOfTheMonth: {
          isLastDay: false,
          customDay: null,
          week: {
            number: null,
            dayOfTheWeek: null,
          },
        },
        deliveryRadius: user?.delivery_radius
          ? new Distance(user?.delivery_radius, 'mi').asRounded(
              defaultDistanceUnit.value
            )
          : null,
        pickupInstructions: '',
        dropoffInstructions: '',
        notifyOnEdit: false,
        additionalImages: [],
        availableOnUnassign: false,
        destLocation: {},
        pickupLocation: !isFoodRun
          ? {
              lat: user.lat,
              lng: user.lng,
              formattedAddress: user.formatted_address,
              name: user.place,
              streetAddress: user.street_address,
              city: user.city,
              province: user.province,
              country: user.country,
              zipCode: user.zip_code,
              placeId: user.place_id,
              timeZoneId: user.timezone_id,
            }
          : {},
        isFoodMover,
        useCustomPickupAddress: false,
        useCustomDestAddress: false,
      };
      reset(freshDefaultValues);
      setHasInitialized(true);
    }
  }, [
    open,
    initialDonation,
    isFoodRun,
    user,
    defaultDistanceUnit,
    isFoodMover,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ]);

  useEffect(() => {
    if (typeof data.useAIToFillDetails === 'boolean' && hasInitialized) {
      // When AI is turned on, clear the remember choice checkbox and any stored preference
      if (data.useAIToFillDetails) {
        setValue('rememberAIChoice', false);
        localStorage.removeItem('aiTogglePreference');
      }

      // When "remember my choice" is checked and AI is off, store the preference
      if (!data.useAIToFillDetails && data.rememberAIChoice) {
        localStorage.setItem('aiTogglePreference', 'off');
      }
    }
  }, [
    data.useAIToFillDetails,
    data.rememberAIChoice,
    setValue,
    isFoodRun,
    hasInitialized,
  ]);

  useEffect(() => {
    if (!data.foodTypes || !data.isPartialRescue) return;

    const allAssignedRecipients = new Set(
      Object.values(data.foodTypes)
        .flatMap((foodType) => foodType.assignedRecipients || [])
        .filter((r) => r.teamId && r.teamId !== 'unassigned')
        .map((r) => r.teamId)
    );

    const newDestinationAddresses = Array.from(allAssignedRecipients)
      .map((teamId) => {
        const existingAddress = data.destinationAddresses.find(
          (addr) => addr.teamId === teamId
        );

        if (
          existingAddress &&
          (data.deliveryOption === 0 || data.deliveryOption === 4)
        ) {
          return existingAddress;
        } else {
          const recipientData = (isFoodRun ? recipients : nearbyNPOs).find(
            (r) => r.team_id === teamId
          );
          if (!recipientData) {
            return null;
          }
          return {
            teamId,
            location: {
              lat: recipientData.lat,
              lng: recipientData.lng,
              formattedAddress: recipientData.formatted_address,
              name: recipientData.place,
              streetAddress: recipientData.street_address,
              city: recipientData.city,
              province: recipientData.province,
              country: recipientData.country,
              zipCode: recipientData.zip_code,
              placeId: recipientData.place_id,
              timeZoneId: recipientData.timezone_id,
            },
          };
        }
      })
      .filter(Boolean);

    setValue('destinationAddresses', newDestinationAddresses);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    data.foodTypes,
    recipients,
    nearbyNPOs,
    isFoodRun,
    JSON.stringify(data.destinationAddresses),
    data.deliveryOption,
  ]);

  const handleAddressChange = ({ teamId, newAddress }) => {
    const updatedDestinationAddresses = data.destinationAddresses.map(
      (addr) => {
        if (addr.teamId === teamId) {
          return {
            teamId,
            location: newAddress,
          };
        }
        return addr;
      }
    );
    setValue('destinationAddresses', updatedDestinationAddresses);
  };

  // Auto-populate pickup instructions from donor organization for food runs
  useEffect(() => {
    if (!isFoodRun || !isFoodMover) return;
    if (data?.donor) {
      (async () => {
        try {
          const donorTeamId = data.donor;
          const pickup = await backend.team.getOrgInstructions(
            donorTeamId,
            'pickup'
          );
          if (!pickupManual) {
            setValue(
              'pickupInstructions',
              typeof pickup === 'string' && pickup.trim().length ? pickup : ''
            );
          }
        } catch (e) {
          if (!pickupManual) {
            setValue('pickupInstructions', '');
          }
        }
      })();
    } else {
      if (!pickupManual) {
        setValue('pickupInstructions', '');
      }
    }
  }, [isFoodRun, isFoodMover, data?.donor, pickupManual, setValue]);

  // Auto-populate dropoff instructions from recipient organization for food runs
  useEffect(() => {
    if (!isFoodRun || !isFoodMover) return;
    if (data?.receiverTeam && data.receiverTeam !== 'unassigned') {
      (async () => {
        try {
          const recipientTeamId = data.receiverTeam;
          const dropoff = await backend.team.getOrgInstructions(
            recipientTeamId,
            'dropoff'
          );
          if (!dropoffManual) {
            setValue(
              'dropoffInstructions',
              typeof dropoff === 'string' && dropoff.trim().length
                ? dropoff
                : ''
            );
          }
        } catch (e) {
          if (!dropoffManual) {
            setValue('dropoffInstructions', '');
          }
        }
      })();
    } else {
      if (!dropoffManual) {
        setValue('dropoffInstructions', '');
      }
    }
  }, [isFoodRun, isFoodMover, data?.receiverTeam, dropoffManual, setValue]);

  // Auto-populate pickup instructions from user's organization for donations with receiver pickup
  useEffect(() => {
    if (isFoodRun || !isFoodMover) return;
    if (data?.deliveryOption === 1) {
      (async () => {
        try {
          const pickup = await backend.team.getOrgInstructions(
            user.team_id,
            'pickup'
          );
          if (!pickupManual) {
            setValue(
              'pickupInstructions',
              typeof pickup === 'string' && pickup.trim().length ? pickup : ''
            );
          }
        } catch (e) {
          if (!pickupManual) {
            setValue('pickupInstructions', '');
          }
        }
      })();
    }
  }, [
    isFoodRun,
    isFoodMover,
    data?.deliveryOption,
    user?.team_id,
    pickupManual,
    setValue,
  ]);

  // Auto-populate dropoff instructions from recipient organization for donations with self-delivery
  useEffect(() => {
    if (isFoodRun || !isFoodMover) return;
    if (data?.deliveryOption !== 1) {
      if (data?.receiverTeam && data.receiverTeam !== 'unassigned') {
        (async () => {
          try {
            const dropoff = await backend.team.getOrgInstructions(
              data.receiverTeam,
              'dropoff'
            );
            if (!dropoffManual) {
              setValue(
                'dropoffInstructions',
                typeof dropoff === 'string' && dropoff.trim().length
                  ? dropoff
                  : ''
              );
            }
          } catch (e) {
            if (!dropoffManual) {
              setValue('dropoffInstructions', '');
            }
          }
        })();
      } else {
        if (!dropoffManual) {
          setValue('dropoffInstructions', '');
        }
      }
    }
  }, [
    isFoodRun,
    isFoodMover,
    data?.deliveryOption,
    data?.receiverTeam,
    dropoffManual,
    setValue,
  ]);

  return (
    <Dialog
      PaperProps={{
        id: 'dialog-create-donation',
        sx: {
          maxHeight: 'calc(100dvh - 32px)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        },
      }}
      maxWidth="md"
      fullWidth
      open={open}
      onClose={() => {}}
      disableEnforceFocus={isTourRunning}
    >
      <Stack
        pt={2}
        px={2}
        direction="row"
        justifyContent="space-between"
        alignItems="center"
      >
        <Tooltip title={t('common.help') || 'Help'}>
          <IconButton
            type="button"
            onClick={handleStartTour}
            size="small"
            sx={{ color: 'text.secondary' }}
          >
            <HelpOutlineIcon />
          </IconButton>
        </Tooltip>
        <IconButton onClick={onClose}>
          <Close />
        </IconButton>
      </Stack>
      <Box
        component="form"
        noValidate
        onKeyDown={handleFormKeyDown}
        onSubmit={handleFormSubmit}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
          width: '100%',
          mx: 'auto',
        }}
      >
        <DialogContent
          sx={{
            pt: 0,
            pb: 2,
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
          }}
        >
          <StepContext.Provider
            value={{ isStep, loading: loading || isWaitingForAnalysis }}
          >
            <StepComponent
              step={STEP.UPLOAD_IMAGE}
              title={
                shouldOfferInventory
                  ? t('donations.inventory-source.combined-title')
                  : user.ai_enabled
                    ? t('donations.smart-photo-upload')
                    : t('donations.no-ai-photo-upload')
              }
              description={
                shouldOfferInventory ? (
                  t('donations.inventory-source.combined-description')
                ) : (
                  <>
                    {!isRepost && !isCopy && user.ai_enabled ? (
                      <>
                        {t('donations.step-1-description-1')}
                        <Tooltip title={t('donations.tooltip')} arrow>
                          <InfoOutlinedIcon fontSize="small" color="action" />
                        </Tooltip>
                      </>
                    ) : null}
                    <br />
                    {t('donations.step-1-description-2')}
                  </>
                )
              }
              imageSrc={
                shouldOfferInventory
                  ? undefined
                  : '/static/illustrations/create-donation/step1.svg'
              }
              sx={shouldOfferInventory ? { maxWidth: '900px' } : undefined}
            >
              {!isRepost && !isCopy && user.ai_enabled ? (
                <Box
                  sx={{
                    mx: 'auto',
                    mt: 2,
                    maxWidth: '650px',
                    width: '100%',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      justifyContent: 'space-between',
                      columnGap: 2,
                      rowGap: 0.5,
                      width: '100%',
                    }}
                  >
                    <Controller
                      control={control}
                      name="useAIToFillDetails"
                      render={({ field }) => {
                        return (
                          <Stack
                            direction="row"
                            alignItems="center"
                            gap={1}
                            sx={{ minWidth: 0, flex: 1 }}
                          >
                            <BetaChip />
                            <Tooltip
                              title={
                                hasSelectedInventory
                                  ? t(
                                      'donations.inventory-source.ai-disabled-hint'
                                    )
                                  : ''
                              }
                            >
                              <span>
                                <FormControlLabel
                                  sx={{ m: 0 }}
                                  disabled={hasSelectedInventory}
                                  control={
                                    <Switch
                                      color="secondary"
                                      checked={
                                        Boolean(field.value) &&
                                        !hasSelectedInventory
                                      }
                                      onChange={(e) => {
                                        const newValue = e.target.checked;
                                        field.onChange(newValue);

                                        if (newValue) {
                                          setValue('rememberAIChoice', false);
                                          localStorage.removeItem(
                                            'aiTogglePreference'
                                          );
                                        }
                                      }}
                                    />
                                  }
                                  label={
                                    <Typography
                                      variant="body2"
                                      component="span"
                                    >
                                      {t('donations.use-ai-to-fill-details')}
                                    </Typography>
                                  }
                                />
                              </span>
                            </Tooltip>
                          </Stack>
                        );
                      }}
                    />
                    {!data.useAIToFillDetails &&
                    !hasSelectedInventory &&
                    !hasStoredPreferenceOnOpen ? (
                      <Controller
                        control={control}
                        name="rememberAIChoice"
                        render={({ field }) => {
                          return (
                            <FormControlLabel
                              sx={{ m: 0, flexShrink: 0 }}
                              control={
                                <Checkbox
                                  color="secondary"
                                  checked={field.value}
                                  onChange={(e) => {
                                    const isChecked = e.target.checked;
                                    field.onChange(isChecked);

                                    if (isChecked) {
                                      localStorage.setItem(
                                        'aiTogglePreference',
                                        'off'
                                      );
                                    } else {
                                      localStorage.removeItem(
                                        'aiTogglePreference'
                                      );
                                    }
                                  }}
                                />
                              }
                              label={
                                <Typography variant="body2" component="span">
                                  {t('donations.remember-ai-choice')}
                                </Typography>
                              }
                            />
                          );
                        }}
                      />
                    ) : null}
                  </Box>
                  {hasSelectedInventory ? (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 0.25 }}
                    >
                      {t('donations.inventory-source.ai-disabled-hint')}
                    </Typography>
                  ) : !data.useAIToFillDetails ? (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 0.25 }}
                    >
                      {t('donations.manual-entry-hint')}
                    </Typography>
                  ) : null}
                </Box>
              ) : null}

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: 'minmax(0, 1fr)',
                    md: shouldOfferInventory
                      ? 'minmax(0, 1fr) minmax(0, 1fr)'
                      : 'minmax(0, 1fr)',
                  },
                  gap: 2,
                  mt: 2.5,
                  alignItems: 'stretch',
                  ...(shouldOfferInventory
                    ? {
                        minHeight: { md: 300 },
                        height: { md: 'min(360px, 46dvh)' },
                      }
                    : {}),
                }}
              >
                <Stack
                  spacing={1}
                  sx={
                    shouldOfferInventory
                      ? {
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 2,
                          p: { xs: 1.5, sm: 2 },
                          minWidth: 0,
                          minHeight: 0,
                          height: '100%',
                          overflow: 'hidden',
                        }
                      : undefined
                  }
                >
                  {shouldOfferInventory ? (
                    <Typography variant="subtitle1" fontWeight={700}>
                      {t('donations.photos')}
                    </Typography>
                  ) : null}
                  <Controller
                    control={control}
                    name="images"
                    render={({ field }) => {
                      return (
                        <DonationUploadFile
                          compact={shouldOfferInventory}
                          error={errors?.images?.message}
                          value={field.value?.map((image) => {
                            const isUrl = typeof image === 'string';
                            if (isUrl) {
                              return getStoredImageUrl(image);
                            }
                            return image.file || image;
                          })}
                          onChange={async (files) => {
                            const processedFiles = await Promise.all(
                              files.map(async (image) => {
                                const isUrl = typeof image === 'string';
                                if (isUrl) {
                                  return image;
                                }
                                const data = await readFile(image);
                                return { file: image, data };
                              })
                            );
                            field.onChange(processedFiles);
                            setCorruptedImageIndices([]);
                          }}
                          isAnalyzing={isWaitingForAnalysis}
                          invalidIndices={corruptedImageIndices}
                          sx={
                            shouldOfferInventory
                              ? { flex: 1, minHeight: 0 }
                              : undefined
                          }
                        />
                      );
                    }}
                  />
                </Stack>

                {shouldOfferInventory ? (
                  <Stack
                    spacing={1}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      p: { xs: 1.5, sm: 2 },
                      minWidth: 0,
                      minHeight: 0,
                      height: '100%',
                      overflow: 'hidden',
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight={700}>
                      {t('donations.inventory-source.short-title')}
                    </Typography>
                    <InventoryDonationSourceStep
                      compact
                      error={inventoryQuery.isError}
                      hasMore={inventoryQuery.data?.hasMore}
                      items={[
                        ...(data.selectedInventoryItems ?? []),
                        ...availableInventoryItems.filter(
                          (item) =>
                            !(data.selectedInventoryItems ?? []).some(
                              (selected) => selected.id === item.id
                            )
                        ),
                      ]}
                      loading={
                        inventoryQuery.isLoading || inventoryQuery.isFetching
                      }
                      onRetry={inventoryQuery.refetch}
                      onSearchChange={setInventorySearch}
                      onToggle={(item) => {
                        const selectedItems = data.selectedInventoryItems ?? [];
                        const nextSelectedItems = selectedItems.some(
                          (selected) => selected.id === item.id
                        )
                          ? selectedItems.filter(
                              (selected) => selected.id !== item.id
                            )
                          : [...selectedItems, { ...item, quantity: 1 }];
                        setValue('selectedInventoryItems', nextSelectedItems);
                        setValue(
                          'donatingFromInventory',
                          nextSelectedItems.length > 0
                        );
                        clearAIAnalysisPrefill();
                        setAnalysisError(null);
                      }}
                      search={inventorySearch}
                      selectedItems={data.selectedInventoryItems ?? []}
                    />
                  </Stack>
                ) : null}
              </Box>

              {analysisError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>
                      {analysisError.failureType === 'timeout'
                        ? t('donations.ai-analysis-timed-out')
                        : t('donations.ai-analysis-failed')}
                    </strong>
                  </Typography>
                </Alert>
              )}
            </StepComponent>
            <StepComponent
              step={STEP.SELECT_FOOD_TYPES}
              dataTour={
                isFoodRun ? 'create-food-run-food-types-step' : undefined
              }
              title={
                data.useAIToFillDetails && data.filledFoodTypes.length > 0
                  ? t('donations.review-detected-categories')
                  : t('donations.select-donation-categories')
              }
              description={t('donations.step-2-description-2')}
              imageSrc="/static/illustrations/create-donation/step2.svg"
            >
              <Container
                sx={{
                  mt: 3,
                  maxWidth: '750px',
                }}
              >
                <DonationFoodTypesSelector
                  includeFoodTypes
                  value={data.foodTypes}
                  onChange={(value) => {
                    const keys = Object.keys(value);
                    setValue(
                      'foodTypeNames',
                      keys.map((key) => key.replaceAll('-', '_')),
                      { shouldValidate: true }
                    );
                    setValue('foodTypes', value, { shouldValidate: true });
                  }}
                  recipients={isFoodRun ? recipients : nearbyNPOs}
                  isPartialRescue={data.isPartialRescue}
                  errors={errors}
                  destinationAddresses={data.destinationAddresses}
                  onAddressChange={handleAddressChange}
                  deliveryOption={data.deliveryOption}
                  setValue={setValue}
                />

                {errors.foodTypeNames?.message && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {errors.foodTypeNames.message}
                  </Alert>
                )}

                {data.useAIToFillDetails && data.filledFoodTypes.length > 0 ? (
                  <Typography
                    variant="body2"
                    color="gray"
                    sx={{ mt: 2, textAlign: 'center' }}
                  >
                    {t('donations.based-on-photo')}{' '}
                    <Typography
                      variant="body2"
                      component="span"
                      color="secondary"
                    >
                      {(data.aiAnalysisCategories ?? [])
                        .map(
                          (id) => foodTypes.find((ft) => ft.id === id)?.title
                        )
                        .join(', ')}
                    </Typography>{' '}
                    {t('donations.selected')}.
                  </Typography>
                ) : null}
              </Container>
            </StepComponent>
            <StepComponent
              step={STEP.TITLE}
              dataTour={isFoodRun ? 'create-food-run-title-step' : undefined}
              title={
                isFoodRun
                  ? t('donations.name-your-food-run')
                  : t('donations.name-your-donation')
              }
              description={
                isFoodRun
                  ? t('donations.step-3-subtitle_food-run')
                  : t('donations.step-3-subtitle_donation')
              }
              imageSrc="/static/illustrations/create-donation/step3.svg"
            >
              <Stack direction="column" spacing={1} mt={2}>
                {data.inventoryDescriptionTruncated ? (
                  <Alert severity="warning">
                    {t('donations.inventory-source.description-truncated')}
                  </Alert>
                ) : null}
                <Controller
                  control={control}
                  name="title"
                  render={({ field }) => (
                    <Stack>
                      <TextField
                        size="medium"
                        label={t('donations.title')}
                        placeholder={t('donations.title-placeholder')}
                        error={!!errors?.title?.message}
                        helperText={errors?.title?.message}
                        {...field}
                        inputRef={titleInputRef}
                        fullWidth
                        inputProps={{ maxLength: 120 }}
                        onChange={(e) => {
                          const value = e.target.value;
                          field.onChange(value);
                        }}
                      />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ alignSelf: 'flex-end', mt: 0.5 }}
                      >
                        {(data?.title || '').length}/120
                      </Typography>
                    </Stack>
                  )}
                />
                {!isFoodRun ? (
                  <Alert severity="info">
                    {t('donations.title-privacy-hint')}
                  </Alert>
                ) : null}
              </Stack>
            </StepComponent>
            <StepComponent
              step={STEP.DESCRIPTION}
              title={
                isFoodRun
                  ? t('donations.add-a-description-to-your-food-run')
                  : t('donations.add-a-description-to-your-donation')
              }
              description={
                isFoodRun
                  ? t('donations.step-4-subtitle_food-run')
                  : t('donations.step-4-subtitle_donation')
              }
              imageSrc="/static/illustrations/create-donation/step4.svg"
            >
              <Stack direction="column" spacing={1} mt={2}>
                <Controller
                  control={control}
                  name="description"
                  render={({ field }) => {
                    return (
                      <Stack>
                        <TextField
                          size="medium"
                          label={t('donations.description')}
                          placeholder={t(
                            isFoodRun
                              ? 'donations.description-placeholder_food-run'
                              : 'donations.description-placeholder_donation'
                          )}
                          error={!!errors?.description?.message}
                          helperText={errors?.description?.message}
                          {...field}
                          inputRef={descriptionInputRef}
                          fullWidth
                          multiline
                          rows={5}
                          inputProps={{ maxLength: 500 }}
                          onChange={(e) => {
                            const value = e.target.value;
                            field.onChange(value);
                          }}
                        />
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ alignSelf: 'flex-end', mt: 0.5 }}
                        >
                          {(data?.description || '').length}/500
                        </Typography>
                      </Stack>
                    );
                  }}
                />
              </Stack>
            </StepComponent>
            <StepComponent
              step={STEP.PARTIAL_RESCUE}
              title={t(
                'donations.is-this-donation-suitable-for-partial-rescue'
              )}
              description={t('donations.step-5-description')}
              imageSrc="/static/illustrations/create-donation/step5.svg"
            >
              <Stack
                mt={2}
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                justifyContent="center"
                sx={{ mx: 'auto', width: '100%' }}
              >
                {[
                  {
                    isSelected: !data.isPartialRescue,
                    icon: (
                      <Box
                        sx={{
                          width: 88,
                          height: 28,
                          borderRadius: 1,
                          backgroundColor: '#298171',
                          opacity: 0.8,
                        }}
                      />
                    ),
                    title: t('donations.must-be-picked-all-at-once'),
                    description: t('donations.full-rescue-description'),
                    onClick: () => {
                      setValue(
                        'foodTypes',
                        generateFoodTypesObj({
                          foodTypeNames: data.foodTypeNames,
                          isPartialRescue: false,
                          existingFoodTypes: data.foodTypes,
                        })
                      );
                      setValue('isPartialRescue', false);
                    },
                  },
                  {
                    isSelected: data.isPartialRescue,
                    icon: (
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Box
                          sx={{
                            width: 28,
                            height: 28,
                            borderRadius: 1,
                            backgroundColor: '#298171',
                            opacity: 0.8,
                          }}
                        />
                        <Box
                          sx={{
                            width: 28,
                            height: 28,
                            borderRadius: 1,
                            backgroundColor: '#F27534',
                            opacity: 0.8,
                          }}
                        />
                        <Box
                          sx={{
                            width: 28,
                            height: 28,
                            borderRadius: 1,
                            backgroundColor: '#4695B7',
                            opacity: 0.8,
                          }}
                        />
                      </Box>
                    ),
                    title: t('donations.partial-rescue-allowed'),
                    description: t('donations.partial-rescue-description'),
                    onClick: () => {
                      setValue(
                        'foodTypes',
                        generateFoodTypesObj({
                          foodTypeNames: data.foodTypeNames,
                          isPartialRescue: true,
                          existingFoodTypes: data.foodTypes,
                        })
                      );
                      setValue('isPartialRescue', true);
                    },
                  },
                ].map((option, idx) => (
                  <Stack
                    component="button"
                    type="button"
                    key={idx}
                    onClick={option.onClick}
                    aria-pressed={option.isSelected}
                    direction="column"
                    alignItems="center"
                    spacing={1.5}
                    sx={{
                      flex: 1,
                      border: '2px solid',
                      borderColor: option.isSelected
                        ? 'secondary.main'
                        : 'divider',
                      boxShadow: option.isSelected
                        ? (t) => `0 0 0 1px ${t.palette.secondary.main}`
                        : 'none',
                      borderRadius: '12px',
                      p: 3,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      ...keyboardCardButtonSx,
                      '&:hover': {
                        borderColor: option.isSelected
                          ? 'secondary.main'
                          : 'grey.400',
                      },
                    }}
                  >
                    {option.icon}
                    <Typography
                      fontWeight="bold"
                      variant="body1"
                      color="primary"
                      textAlign="center"
                    >
                      {option.title}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      textAlign="center"
                    >
                      {option.description}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </StepComponent>
            <StepComponent
              step={STEP.TIME_RECURRING}
              title={
                isFoodRun
                  ? t('donations.schedule-your-food-run')
                  : t('donations.schedule-your-donation')
              }
              description={t('donations.schedule-subtitle')}
              imageSrc="/static/illustrations/create-donation/step8.svg"
            >
              {!canCreateRecurring && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    mb: 2,
                    mt: 2,
                  }}
                >
                  <Button
                    component={Link}
                    to={PATH_DASHBOARD.allPlans}
                    variant="contained"
                    color="secondary"
                    sx={{
                      borderRadius: 1,
                      px: 2,
                      py: 0.5,
                      color: 'white',
                      textTransform: 'none',
                      fontWeight: 'bold',
                      fontSize: '14px',
                      boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                      '&:hover': {
                        boxShadow: '0 6px 12px rgba(0,0,0,0.15)',
                      },
                    }}
                    endIcon={<OpenInNew />}
                  >
                    {t('settings.billing.upgrade-now')}
                  </Button>
                </Box>
              )}
              <DonationScheduleFields
                control={control}
                data={data}
                errors={errors}
                setValue={setValue}
                isFoodRun={isFoodRun}
                canCreateRecurring={canCreateRecurring}
                user={user}
              />
            </StepComponent>
            <StepComponent
              step={STEP.DELIVERY_METHOD}
              dataTour={isFoodRun ? 'create-food-run-donor-step' : undefined}
              title={
                isFoodRun
                  ? t('donations.step-delivery-option-title', {
                      context: 'food-run',
                    })
                  : t('donations.step-delivery-option-title', {
                      context: 'donation',
                    })
              }
              description={
                isFoodRun
                  ? t('donations.step-delivery-option-description', {
                      context: 'food-run',
                    })
                  : t('donations.step-delivery-option-description', {
                      context: 'donation',
                    })
              }
              imageSrc={
                isFoodRun
                  ? '/static/illustrations/create-donation/vehicle-and-driver.svg'
                  : '/static/illustrations/create-donation/handover-box.svg'
              }
            >
              <Stack mt={3}>
                {!isFoodRun && (
                  <Controller
                    control={control}
                    name="deliveryOption"
                    render={({ field }) => (
                      <FormControl>
                        <InputLabel>
                          {t('donations.delivery-option')}
                        </InputLabel>
                        <Select
                          label={t('donations.delivery-option')}
                          {...field}
                          onChange={(e) => {
                            field.onChange(e);
                            if (e.target.value === 4) {
                              setValue('isPartialRescue', false);
                            }
                          }}
                        >
                          <MenuItem value={0}>
                            {t('donations.self-deliver')}
                          </MenuItem>
                          <MenuItem value={1}>
                            {t('donations.reciever-picks-up')}
                          </MenuItem>
                          {(plan === 'basic' || isFoodMover) &&
                            data.occurrenceType === 'onetime' &&
                            !user?.is_driver && (
                              <MenuItem value={4}>
                                {t('donations.staff-meals')}
                              </MenuItem>
                            )}
                        </Select>
                      </FormControl>
                    )}
                  />
                )}
                {data.deliveryOption === 4 && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    {t('donations.staff-meals-info-message')}
                  </Alert>
                )}

                {isFoodRun && (
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ mt: 2 }}
                  >
                    <Controller
                      control={control}
                      name="donor"
                      render={({ field }) => {
                        const options = donorsList.filter((d) => d.team_id);
                        const value =
                          options.find(
                            (option) => option.team_id === field.value
                          ) || null;

                        return (
                          <Autocomplete
                            value={value}
                            onChange={(_event, newValue) =>
                              field.onChange(newValue?.team_id)
                            }
                            freeSolo
                            options={options}
                            isOptionEqualToValue={(option, value) =>
                              option.team_id === value.team_id
                            }
                            getOptionKey={(option) => option.team_id}
                            getOptionLabel={(option) => option.name || ''}
                            renderOption={(props, option) => {
                              const { key, ...optionProps } = props;
                              return (
                                <Box
                                  key={key}
                                  {...optionProps}
                                  display="flex"
                                  flexDirection="column"
                                  mb={1}
                                >
                                  <Typography
                                    variant="body1"
                                    alignSelf="flex-start"
                                  >
                                    {option.name}
                                  </Typography>
                                  <Typography
                                    color="text.secondary"
                                    variant="overline"
                                    alignSelf={'flex-start'}
                                    fontSize={12}
                                  >
                                    {option.formatted_address || option.email}
                                  </Typography>
                                </Box>
                              );
                            }}
                            fullWidth
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label={t('donations.donor')}
                                error={!!errors?.donor?.message}
                                helperText={errors?.donor?.message}
                              />
                            )}
                          />
                        );
                      }}
                    />
                    <IconButton
                      onClick={refreshDonors}
                      size="small"
                      sx={{ mt: 1 }}
                    >
                      <Refresh />
                    </IconButton>
                  </Stack>
                )}
                {isFoodRun && data.donor && !data.useCustomPickupAddress && (
                  <Typography
                    variant="body2"
                    sx={{ mt: 1, cursor: 'pointer', color: 'primary.main' }}
                    onClick={() => {
                      setValue('useCustomPickupAddress', true);
                      const selectedDonor = donors.find(
                        (team) => team.team_id === data.donor
                      );
                      if (selectedDonor) {
                        setValue('pickupLocation', {
                          formattedAddress: selectedDonor.formatted_address,
                        });
                      }
                    }}
                  >
                    {t('donations.change-pickup-address')}
                  </Typography>
                )}
                {user.rescue_time_tracking_enabled &&
                (isFoodRun ||
                  (data.deliveryOption === 0 &&
                    (plan === 'basic' || isFoodMover))) ? (
                  <Controller
                    control={control}
                    name="estimatedRescueTimeMinutes"
                    render={({ field, fieldState }) => (
                      <TextField
                        sx={{
                          mt: 2,
                        }}
                        {...field}
                        type="number"
                        inputProps={{ min: 1, max: 720 }}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                        label={t('donations.estimated-rescue-time-minutes')}
                      />
                    )}
                  />
                ) : null}
                {!isFoodRun &&
                  data.deliveryOption === 1 &&
                  !data.useCustomPickupAddress && (
                    <Typography
                      variant="body2"
                      sx={{ mt: 1, cursor: 'pointer', color: 'primary.main' }}
                      onClick={() => {
                        setValue('useCustomPickupAddress', true);
                        setValue('pickupLocation', {
                          formattedAddress: user.formatted_address,
                        });
                      }}
                    >
                      {t('donations.change-pickup-address')}
                    </Typography>
                  )}
                {!isFoodRun &&
                  data.deliveryOption === 0 &&
                  !data.useCustomPickupAddress && (
                    <Typography
                      variant="body2"
                      sx={{ mt: 1, cursor: 'pointer', color: 'primary.main' }}
                      onClick={() => {
                        setValue('useCustomPickupAddress', true);
                        setValue('pickupLocation', {
                          formattedAddress: user.formatted_address,
                        });
                      }}
                    >
                      {t('donations.change-pickup-address')}
                    </Typography>
                  )}
                {data.useCustomPickupAddress ? (
                  <Box mt={2}>
                    <Controller
                      control={control}
                      name="pickupLocation"
                      render={({ field }) => (
                        <AddressInputWithoutMap
                          {...field}
                          label={
                            isFoodRun
                              ? t('donations.custom-pickup-address')
                              : t('donations.donation-location')
                          }
                          onChange={(value) => {
                            field.onChange(value);
                          }}
                        />
                      )}
                    />
                    {data.useCustomPickupAddress && (
                      <Typography
                        variant="body2"
                        sx={{
                          mt: 1,
                          mb: 2,
                          cursor: 'pointer',
                          color: 'primary.main',
                        }}
                        onClick={() => {
                          setValue('useCustomPickupAddress', false);
                          if (!isFoodRun) {
                            setValue('pickupLocation', {
                              lat: user.lat,
                              lng: user.lng,
                              formattedAddress: user.formatted_address,
                              name: user.place,
                              streetAddress: user.street_address,
                              city: user.city,
                              province: user.province,
                              country: user.country,
                              zipCode: user.zip_code,
                              placeId: user.place_id,
                              timeZoneId: user.timezone_id,
                            });
                          }
                        }}
                      >
                        {t('donations.use-default-address')}
                      </Typography>
                    )}
                  </Box>
                ) : null}
              </Stack>
            </StepComponent>
            {data.deliveryOption !== 4 && (
              <StepComponent
                step={STEP.LOGISTICS}
                dataTour={
                  isFoodRun ? 'create-food-run-logistics-step' : undefined
                }
                title={t('donations.step-logistics-title')}
                description={t('donations.step-logistics-subtitle', {
                  context: isFoodRun
                    ? 'food-run'
                    : data.deliveryOption === 0
                      ? 'donation-deliver'
                      : 'donation-pickup',
                })}
                imageSrc={
                  isFoodRun
                    ? '/static/illustrations/create-donation/vehicle-and-driver.svg'
                    : '/static/illustrations/create-donation/handover-box.svg'
                }
              >
                <Stack mt={3}>
                  {!isFoodRun &&
                    ENABLE_CREATE_DONATION_PARTIAL &&
                    data.isPartialRescue && (
                      <Alert severity="info" sx={{ mb: 2 }}>
                        {t('donations.partial-rescue-receivers-info')}
                      </Alert>
                    )}
                  {((isFoodRun && data.donor) ||
                    (!isFoodRun &&
                      data.deliveryOption !== 4 &&
                      (!ENABLE_CREATE_DONATION_PARTIAL ||
                        !data.isPartialRescue))) && (
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{ mt: 2 }}
                    >
                      <Controller
                        control={control}
                        name="receiverTeam"
                        render={({ field }) => {
                          const options = availableRecipientOptions;
                          const showMatchGroups =
                            isFoodRun &&
                            options.some(
                              (option) =>
                                option.recipient_match_group &&
                                option.recipient_match_group !== 'other'
                            );
                          const value =
                            options.find(
                              (option) => option.team_id === field.value
                            ) || null;
                          const bestMatchesLabel = getRecipientMatchGroupLabel(
                            { recipient_match_group: 'best' },
                            t
                          );

                          return (
                            <Autocomplete
                              value={value}
                              onChange={(_event, newValue) =>
                                field.onChange(
                                  newValue?.team_id ?? 'unassigned'
                                )
                              }
                              options={options}
                              loading={
                                isFoodRun
                                  ? recipientMatchesLoading
                                  : nearbyNPOsLoading
                              }
                              groupBy={
                                showMatchGroups
                                  ? (option) =>
                                      getRecipientMatchGroupLabel(option, t)
                                  : undefined
                              }
                              renderGroup={
                                showMatchGroups
                                  ? renderRecipientMatchGroup({
                                      noteGroup: bestMatchesLabel,
                                      note: getRecipientMatchNote(options, t),
                                    })
                                  : undefined
                              }
                              filterOptions={
                                isFoodRun
                                  ? (allOptions, state) =>
                                      capRecipientOptions(
                                        filterRecipientOptions(
                                          allOptions,
                                          state
                                        ),
                                        {
                                          getGroup: (option) =>
                                            option.recipient_match_group,
                                          makeHint: (count) => ({
                                            team_id: '__more__',
                                            recipient_match_group: 'other',
                                            more_hint: getRecipientMoreHint(
                                              count,
                                              t
                                            ),
                                          }),
                                        }
                                      )
                                  : undefined
                              }
                              getOptionDisabled={(option) =>
                                option.onboarding?.isPending === true ||
                                Boolean(option.more_hint)
                              }
                              isOptionEqualToValue={(option, value) =>
                                option.team_id === value.team_id
                              }
                              getOptionKey={(option) => option.team_id}
                              getOptionLabel={(option) => option.name || ''}
                              renderOption={(props, option) => {
                                const { key, ...optionProps } = props;
                                if (option.more_hint) {
                                  return (
                                    <RecipientMoreHint
                                      key={key}
                                      {...optionProps}
                                      label={option.more_hint}
                                    />
                                  );
                                }
                                const matchLabels = isFoodRun
                                  ? getRecipientMatchLabels(option, t)
                                  : null;
                                return (
                                  <Box
                                    key={key}
                                    {...optionProps}
                                    display="flex"
                                    flexDirection="column"
                                    mb={1}
                                  >
                                    <Typography
                                      variant="body1"
                                      alignSelf="flex-start"
                                    >
                                      {option.animal_food_rescuer_type ? (
                                        <Pets fontSize="small" sx={{ mr: 1 }} />
                                      ) : null}
                                      {option.name}
                                    </Typography>
                                    <Typography
                                      color="text.secondary"
                                      variant="overline"
                                      alignSelf={'flex-start'}
                                      fontSize={12}
                                    >
                                      {option.formatted_address || option.email}
                                    </Typography>
                                    <RecipientMatchCaption
                                      labels={matchLabels}
                                    />
                                    <TaxExemptBadge
                                      org={option}
                                      sx={{ alignSelf: 'flex-start', mt: 0.5 }}
                                    />
                                    {option.onboarding?.isPending && (
                                      <Typography
                                        color="warning.main"
                                        variant="caption"
                                        alignSelf="flex-start"
                                      >
                                        {t(
                                          'donations.pending-onboarding-status'
                                        )}
                                      </Typography>
                                    )}
                                  </Box>
                                );
                              }}
                              fullWidth
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label={receiverLabel}
                                  error={!!errors?.receiverTeam?.message}
                                  helperText={receiverHelperText}
                                  FormHelperTextProps={
                                    receiverHelperIsWarning
                                      ? { sx: { color: 'warning.main' } }
                                      : undefined
                                  }
                                  InputProps={{
                                    ...params.InputProps,
                                    // The previous ranking stays listed while
                                    // a re-match runs, so show progress here.
                                    endAdornment: (
                                      <>
                                        {isFoodRun &&
                                        recipientMatchesLoading ? (
                                          <CircularProgress
                                            color="inherit"
                                            size={18}
                                          />
                                        ) : null}
                                        {params.InputProps.endAdornment}
                                      </>
                                    ),
                                  }}
                                />
                              )}
                            />
                          );
                        }}
                      />
                      <IconButton
                        onClick={
                          isFoodRun ? refreshRecipients : refreshNearbyNPOs
                        }
                        size="small"
                        sx={{ mt: 1 }}
                      >
                        <Refresh />
                      </IconButton>
                    </Stack>
                  )}

                  {isFoodRun &&
                    data.donor &&
                    data.receiverTeam &&
                    data.receiverTeam !== 'unassigned' &&
                    !data.useCustomDestAddress && (
                      <Typography
                        variant="body2"
                        sx={{
                          mt: 1,
                          mb: 2,
                          cursor: 'pointer',
                          color: 'primary.main',
                        }}
                        onClick={() => {
                          setValue('useCustomDestAddress', true);
                          const selectedReceiver = isFoodRun
                            ? recipientsList.find(
                                (team) => team.team_id === data.receiverTeam
                              )
                            : nearbyNPOs.find(
                                (team) => team.team_id === data.receiverTeam
                              );
                          if (selectedReceiver) {
                            setValue('destLocation', {
                              formattedAddress:
                                selectedReceiver.formatted_address,
                            });
                          }
                        }}
                      >
                        {t('donations.change-delivery-address')}
                      </Typography>
                    )}
                  {!isFoodRun &&
                    data.deliveryOption === 0 &&
                    data.receiverTeam &&
                    data.receiverTeam !== 'unassigned' &&
                    !data.useCustomDestAddress && (
                      <Typography
                        variant="body2"
                        sx={{ mt: 1, cursor: 'pointer', color: 'primary.main' }}
                        onClick={() => {
                          setValue('useCustomDestAddress', true);
                          const selectedReceiver = nearbyNPOs.find(
                            (team) => team.team_id === data.receiverTeam
                          );
                          if (selectedReceiver) {
                            setValue('destLocation', {
                              formattedAddress:
                                selectedReceiver.formatted_address,
                            });
                          }
                        }}
                      >
                        {t('donations.change-delivery-address')}
                      </Typography>
                    )}
                  {data.useCustomDestAddress && (
                    <Box mt={2}>
                      <Controller
                        control={control}
                        name="destLocation"
                        render={({ field }) => (
                          <AddressInputWithoutMap
                            {...field}
                            label={
                              isFoodRun
                                ? t('donations.custom-destination-address')
                                : t('donations.destination-location')
                            }
                            onChange={(value) => {
                              field.onChange(value);
                            }}
                          />
                        )}
                      />
                      {data.useCustomDestAddress && (
                        <Typography
                          variant="body2"
                          sx={{
                            mt: 1,
                            cursor: 'pointer',
                            color: 'primary.main',
                          }}
                          onClick={() =>
                            setValue('useCustomDestAddress', false)
                          }
                        >
                          {t('donations.use-default-address')}
                        </Typography>
                      )}
                    </Box>
                  )}

                  {!isFoodRun &&
                  data.deliveryOption === 0 &&
                  (data.isPartialRescue
                    ? Object.values(data.foodTypes).some((foodType) =>
                        foodType?.assignedRecipients?.some(
                          (r) => r.teamId === 'unassigned'
                        )
                      )
                    : !data.receiverTeam ||
                      data.receiverTeam === 'unassigned') ? (
                    <Controller
                      control={control}
                      name="deliveryRadius"
                      render={({ field, fieldState }) => (
                        <TextField
                          sx={{
                            mt: 2,
                          }}
                          {...field}
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                          label={t('donations.delivery-radius', {
                            unit: defaultDistanceUnit.label,
                          })}
                        />
                      )}
                    />
                  ) : null}

                  {shouldShowDriverSelector(data) ? (
                    ENABLE_MULTIPLE_DRIVERS ? (
                      <MultipleDriversSelector
                        sx={{ mt: 2 }}
                        drivers={drivers}
                        driversValue={data.drivers}
                        onChange={(value) => {
                          setValue('drivers', value, { shouldValidate: true });
                        }}
                        vehicles={vehicles}
                        error={errors?.drivers?.message}
                      />
                    ) : (
                      <SingleDriverSelector
                        sx={{ mt: 2 }}
                        errors={errors}
                        drivers={drivers}
                        vehicles={vehicles}
                        driverValue={data.driverId}
                        onChangeDriver={(value) => {
                          setValue('driverId', value);
                        }}
                        vehicleValue={data.vehicle}
                        onChangeVehicle={(value) => {
                          setValue('vehicle', value);
                        }}
                        driverTooltip={t('donations.driver-tooltip')}
                      />
                    )
                  ) : null}

                  <Stack mt={3}>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      {t('donations.recommended-vehicle-size')}
                    </Typography>
                    <CardSelector
                      items={vehicleTypes.map((v) => ({
                        name: v.label,
                        imageSrc: `/static/illustrations/create-donation/vehicle-${v.vehicle.toLowerCase()}.svg`,
                        isSelected: data.vehicleSize === v.value,
                        imageWidth: '120px',
                        fontSize: '14px',
                        onClick: () => {
                          setValue('vehicleSize', v.value);
                        },
                      }))}
                    />
                  </Stack>
                </Stack>
              </StepComponent>
            )}
            {(isFoodRun || data.deliveryOption !== 4) && (
              <StepComponent
                step={STEP.INSTRUCTIONS}
                title={
                  isFoodRun
                    ? t('donations.instructions-title_food-run')
                    : t(
                        data.deliveryOption === 1
                          ? 'donations.instructions-title_pickup'
                          : 'donations.instructions-title_dropoff'
                      )
                }
                description={
                  isFoodRun
                    ? t('donations.instructions-description_food-run')
                    : t(
                        data.deliveryOption === 1
                          ? 'donations.instructions-description_pickup'
                          : 'donations.instructions-description_dropoff'
                      )
                }
                imageSrc="/static/illustrations/create-donation/instructions.svg"
              >
                <Stack mt={2} spacing={2}>
                  {isFoodRun ? (
                    <>
                      <Controller
                        control={control}
                        name="pickupInstructions"
                        render={({ field }) => (
                          <>
                            <TextField
                              size="medium"
                              label={t('donations.pickup-instructions')}
                              fullWidth
                              multiline
                              rows={4}
                              {...field}
                              inputRef={pickupInstructionsInputRef}
                              onChange={(e) => {
                                setPickupManual(true);
                                field.onChange(e);
                              }}
                            />
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ mt: 0.5, display: 'block' }}
                            >
                              {t('donations.instructions-used-for-new-pickups')}
                            </Typography>
                          </>
                        )}
                      />
                      <Controller
                        control={control}
                        name="dropoffInstructions"
                        render={({ field }) => (
                          <>
                            <TextField
                              size="medium"
                              label={t('donations.dropoff-instructions')}
                              fullWidth
                              multiline
                              rows={4}
                              {...field}
                              inputRef={dropoffInstructionsInputRef}
                              onChange={(e) => {
                                setDropoffManual(true);
                                field.onChange(e);
                              }}
                            />
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ mt: 0.5, display: 'block' }}
                            >
                              {t(
                                'donations.instructions-used-for-new-dropoffs'
                              )}
                            </Typography>
                          </>
                        )}
                      />
                    </>
                  ) : (
                    <>
                      {data.deliveryOption === 1 ? (
                        <Controller
                          control={control}
                          name="pickupInstructions"
                          render={({ field }) => (
                            <TextField
                              size="medium"
                              label={t('donations.pickup-instructions')}
                              fullWidth
                              multiline
                              rows={4}
                              {...field}
                              inputRef={pickupInstructionsInputRef}
                              onChange={(e) => {
                                setPickupManual(true);
                                field.onChange(e);
                              }}
                            />
                          )}
                        />
                      ) : (
                        <Controller
                          control={control}
                          name="dropoffInstructions"
                          render={({ field }) => (
                            <TextField
                              size="medium"
                              label={t('donations.dropoff-instructions')}
                              fullWidth
                              multiline
                              rows={4}
                              {...field}
                              inputRef={dropoffInstructionsInputRef}
                              onChange={(e) => {
                                setDropoffManual(true);
                                field.onChange(e);
                              }}
                            />
                          )}
                        />
                      )}
                    </>
                  )}

                  <Controller
                    control={control}
                    name="additionalImages"
                    render={({ field }) => (
                      <UploadImagesComponent
                        label={t('donations.additional-images')}
                        value={field.value}
                        onChange={field.onChange}
                        displayLabel
                      />
                    )}
                  />

                  {isFoodRun && (
                    <Controller
                      name="notifyOnEdit"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={
                            <Switch
                              checked={field.value}
                              onChange={(e) => field.onChange(e.target.checked)}
                            />
                          }
                          label={t('donations.notify-if-changes-made-on-edit')}
                        />
                      )}
                    />
                  )}

                  {isFoodRun &&
                    (ENABLE_MULTIPLE_DRIVERS
                      ? Array.isArray(data.drivers) && data.drivers.length > 0
                      : data.driverId && data.driverId !== 'unassigned') && (
                      <Controller
                        name="availableOnUnassign"
                        control={control}
                        render={({ field }) => (
                          <FormControlLabel
                            control={
                              <Switch
                                checked={field.value}
                                onChange={(e) =>
                                  field.onChange(e.target.checked)
                                }
                              />
                            }
                            label={t('donations.make-available-on-unassign')}
                          />
                        )}
                      />
                    )}
                </Stack>
              </StepComponent>
            )}
            <StepComponent
              step={STEP.RECAP}
              title={
                isFoodRun
                  ? t('donations.great-work-here-is-a-recap', {
                      context: 'food-run',
                    })
                  : t('donations.great-work-here-is-a-recap', {
                      context: 'donation',
                    })
              }
              description={t('donations.recap-description')}
              imageSrc="/static/illustrations/create-donation/recap.svg"
            >
              {(() => {
                const donorName = isFoodRun
                  ? donorsList.find((d) => d.team_id === data.donor)?.name
                  : user.business_name || user.name;
                const recipientOptions = isFoodRun
                  ? recipientsList
                  : nearbyNPOs;
                const recipientName = recipientOptions.find(
                  (r) => r.team_id === data.receiverTeam
                )?.name;
                const getRecipientAllocationName = (
                  recipient = {},
                  hasNamedAllocation = false
                ) => {
                  if (recipient.teamId === 'unassigned') {
                    return hasNamedAllocation
                      ? t('donations.available-to-others')
                      : t('donations.available-to-recipients');
                  }

                  if (!recipient.teamId) {
                    return t('donations.unassigned');
                  }

                  return (
                    recipient.name ||
                    recipientOptions.find(
                      (option) => option.team_id === recipient.teamId
                    )?.name ||
                    recipient.teamId
                  );
                };
                const formatRecapAmount = (value) => {
                  const number = Number(value) || 0;
                  return Number.isInteger(number)
                    ? number
                    : Number(number.toFixed(2));
                };
                const buildDateTimeSummary = () => {
                  if (!data.startDate || !data.startTime) return null;

                  const startDate = moment(data.startDate);
                  const startTime = moment(data.startTime);
                  const endDate = data.endDate ? moment(data.endDate) : null;
                  const endTime = data.endTime ? moment(data.endTime) : null;
                  const formatDate = (date) =>
                    formatDisplayDate(date, 'weekdayDateLong');
                  const formatTime = (time) => formatDisplayDate(time, 'time');
                  const buildRow = (label, date, time) => ({
                    label,
                    dateText: formatDate(date),
                    timeText: time ? formatTime(time) : null,
                  });

                  if (data.occurrenceType !== 'onetime') {
                    if (
                      data.deliveryOption !== 4 &&
                      endDate &&
                      endTime &&
                      data.occurrenceType !== 'indefinite'
                    ) {
                      return {
                        rows: [
                          buildRow(t('donations.start'), startDate, startTime),
                          buildRow(t('donations.end'), endDate, endTime),
                        ],
                      };
                    }

                    return {
                      rows: [
                        buildRow(t('donations.start'), startDate, startTime),
                      ],
                    };
                  }

                  if (data.deliveryOption === 4 || !endTime) {
                    return {
                      rows: [
                        buildRow(
                          data.deliveryOption === 4
                            ? t('donations.date')
                            : t('donations.start'),
                          startDate,
                          startTime
                        ),
                      ],
                    };
                  }

                  return {
                    rows: [
                      buildRow(t('donations.start'), startDate, startTime),
                      buildRow(
                        t('donations.end'),
                        isFoodRun && data.occurrenceType === 'onetime'
                          ? startDate
                          : endDate || startDate,
                        endTime
                      ),
                    ],
                  };
                };
                const dateTimeSummary = buildDateTimeSummary();

                // Check if pickup address differs from organization's default
                const selectedDonor = isFoodRun
                  ? donorsList.find((d) => d.team_id === data.donor)
                  : null;
                const pickupAddressDiffers = isFoodRun
                  ? selectedDonor &&
                    data.pickupLocation?.formattedAddress !==
                      selectedDonor.formatted_address
                  : data.pickupLocation?.formattedAddress !==
                    user.formatted_address;

                // Check if delivery address differs from recipient's default
                const selectedRecipient = (
                  isFoodRun ? recipientsList : nearbyNPOs
                ).find((r) => r.team_id === data.receiverTeam);
                const deliveryAddressDiffers =
                  selectedRecipient &&
                  data.destLocation?.formattedAddress !==
                    selectedRecipient.formatted_address;

                return (
                  <Card
                    sx={{
                      mt: 2,
                      maxWidth: '800px',
                      mx: 'auto',
                      background:
                        'linear-gradient(135deg, #eeeeee 0%, #ffffff 100%)',
                    }}
                    elevation={2}
                  >
                    <CardContent>
                      {/* Header with Title and Edit Buttons */}
                      <Box
                        display="flex"
                        flexDirection={{ xs: 'column', sm: 'row' }}
                        justifyContent="space-between"
                        alignItems={{ xs: 'stretch', sm: 'flex-start' }}
                        gap={1}
                        mb={2}
                      >
                        <Box flex={1}>
                          <Typography
                            variant="h5"
                            color="primary"
                            fontWeight="bold"
                            gutterBottom
                          >
                            {data.title}
                          </Typography>
                          <Typography variant="body1" color="text.secondary">
                            {data.description}
                          </Typography>
                        </Box>
                        <Box
                          display="flex"
                          gap={1}
                          alignSelf={{ xs: 'flex-end', sm: 'auto' }}
                        >
                          <IconButton
                            onClick={() => setStep(stepIndices[STEP.TITLE])}
                            size="small"
                          >
                            <Edit />
                          </IconButton>
                          <IconButton
                            onClick={() =>
                              setStep(stepIndices[STEP.DESCRIPTION])
                            }
                            size="small"
                          >
                            <Description />
                          </IconButton>
                        </Box>
                      </Box>

                      {/* Food Types and Photos - Responsive Layout */}
                      <Box
                        display="flex"
                        flexDirection={{ xs: 'column', md: 'row' }}
                        gap={3}
                        mb={3}
                        alignItems={{ xs: 'stretch', md: 'flex-start' }}
                      >
                        {/* Food Types */}
                        <Box flex={1}>
                          <Box display="flex" alignItems="flex-start" gap={2}>
                            <Restaurant
                              sx={{
                                color: 'action.active',
                                fontSize: 16,
                                mt: 0.5,
                              }}
                            />
                            <Box flex={1}>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Typography
                                  variant="subtitle2"
                                  fontWeight="bold"
                                >
                                  {t('donations.food-types')}
                                </Typography>
                                <IconButton
                                  onClick={() =>
                                    setStep(stepIndices[STEP.SELECT_FOOD_TYPES])
                                  }
                                  size="small"
                                >
                                  <Edit />
                                </IconButton>
                              </Box>
                              <Box sx={{ mt: 1 }}>
                                {Array.isArray(data.foodTypeNames) &&
                                data.foodTypeNames.length > 0 ? (
                                  <Stack spacing={1}>
                                    {data.foodTypeNames.map((type, idx) => {
                                      const normalizedType = type.replaceAll(
                                        '_',
                                        '-'
                                      );
                                      const foodTypeObj = foodTypes.find(
                                        (f) => f.id === normalizedType
                                      );
                                      const weight =
                                        data.foodTypes?.[normalizedType];
                                      const assignedRecipients =
                                        data.isPartialRescue
                                          ? weight?.assignedRecipients || []
                                          : [];
                                      const visibleAssignedRecipients =
                                        assignedRecipients.filter(
                                          (recipient) =>
                                            recipient.teamId &&
                                            parseFloat(
                                              recipient.reservedWeight || 0
                                            ) > 0
                                        );
                                      const hasNamedAllocation =
                                        visibleAssignedRecipients.some(
                                          (recipient) =>
                                            recipient.teamId !== 'unassigned'
                                        );
                                      const weightText = weight
                                        ? `${weight.value} ${weight.unit}`
                                        : t('donations.not-set');
                                      return (
                                        <Box
                                          key={idx}
                                          sx={{
                                            borderBottom: '1px solid',
                                            borderColor: 'divider',
                                            pb: 1,
                                          }}
                                        >
                                          <Stack
                                            direction="row"
                                            alignItems="center"
                                            justifyContent="space-between"
                                            gap={1}
                                          >
                                            <Stack
                                              direction="row"
                                              alignItems="center"
                                              gap={1}
                                              sx={{ minWidth: 0, flex: 1 }}
                                            >
                                              {foodTypeObj?.icon && (
                                                <foodTypeObj.icon
                                                  sx={{
                                                    fontSize: 16,
                                                    flexShrink: 0,
                                                  }}
                                                />
                                              )}
                                              <Typography
                                                variant="body2"
                                                noWrap
                                              >
                                                {foodTypeObj?.title || type}
                                              </Typography>
                                              <Chip
                                                size="small"
                                                label={weightText}
                                                variant="outlined"
                                                sx={{
                                                  flexShrink: 0,
                                                  height: 24,
                                                  borderRadius: 1,
                                                  fontWeight: 700,
                                                  bgcolor: 'background.paper',
                                                }}
                                              />
                                            </Stack>
                                            <IconButton
                                              onClick={() =>
                                                setStep(
                                                  stepIndices[
                                                    STEP.SELECT_FOOD_TYPES
                                                  ]
                                                )
                                              }
                                              size="small"
                                              sx={{ flexShrink: 0 }}
                                            >
                                              <Edit />
                                            </IconButton>
                                          </Stack>
                                          {visibleAssignedRecipients.length >
                                            0 && (
                                            <Stack
                                              spacing={0.75}
                                              sx={{ mt: 1, pl: 3 }}
                                            >
                                              {visibleAssignedRecipients.map(
                                                (recipient, recipientIdx) => {
                                                  const isOpenAmount =
                                                    recipient.teamId ===
                                                    'unassigned';
                                                  return (
                                                    <Box
                                                      key={`${type}-${recipient.teamId || 'unassigned'}-${recipientIdx}`}
                                                      sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent:
                                                          'space-between',
                                                        gap: 1,
                                                        px: 1,
                                                        py: 0.75,
                                                        borderRadius: 1,
                                                        bgcolor: isOpenAmount
                                                          ? 'primary.lighter'
                                                          : 'grey.50',
                                                        border: '1px solid',
                                                        borderColor:
                                                          isOpenAmount
                                                            ? 'primary.main'
                                                            : 'divider',
                                                      }}
                                                    >
                                                      <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                        sx={{
                                                          minWidth: 0,
                                                          fontWeight:
                                                            isOpenAmount
                                                              ? 700
                                                              : 500,
                                                        }}
                                                      >
                                                        {getRecipientAllocationName(
                                                          recipient,
                                                          hasNamedAllocation
                                                        )}
                                                      </Typography>
                                                      <Box
                                                        component="span"
                                                        sx={{
                                                          flexShrink: 0,
                                                          px: isOpenAmount
                                                            ? 0.75
                                                            : 0,
                                                          py: isOpenAmount
                                                            ? 0.25
                                                            : 0,
                                                          borderRadius: 1,
                                                          bgcolor: isOpenAmount
                                                            ? 'primary.main'
                                                            : 'transparent',
                                                          color: isOpenAmount
                                                            ? 'primary.contrastText'
                                                            : 'text.primary',
                                                          fontSize: '0.75rem',
                                                          lineHeight: 1.4,
                                                          fontWeight: 700,
                                                          whiteSpace: 'nowrap',
                                                        }}
                                                      >
                                                        {formatRecapAmount(
                                                          recipient.reservedWeight
                                                        )}{' '}
                                                        {weight?.unit || ''}
                                                      </Box>
                                                    </Box>
                                                  );
                                                }
                                              )}
                                            </Stack>
                                          )}
                                        </Box>
                                      );
                                    })}
                                  </Stack>
                                ) : (
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    {t('donations.no-food-types-selected')}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </Box>
                        </Box>

                        {data.images.length > 0 && (
                          <Box sx={{ width: 200, height: 200, flexShrink: 0 }}>
                            <Box
                              sx={{ fontWeight: 700, mb: 1 }}
                              component="div"
                            >
                              {t('donations.photos')}
                            </Box>
                            <Box
                              sx={{
                                display: 'flex',
                                gap: 1,
                                width: '100%',
                                height: 200,
                                overflowX: 'auto',
                                '&::-webkit-scrollbar': { display: 'none' },
                              }}
                            >
                              {data.images.map((image, idx) => {
                                const isUrl = typeof image === 'string';
                                const urlFromFile = isUrl
                                  ? getStoredImageUrl(image)
                                  : URL.createObjectURL(image.file);
                                return (
                                  <Box
                                    key={`food-${idx}`}
                                    component="img"
                                    src={urlFromFile}
                                    alt={`donation-food-img-${idx}`}
                                    sx={{
                                      minWidth: 200,
                                      height: 200,
                                      objectFit: 'cover',
                                      borderRadius: 2,
                                    }}
                                  />
                                );
                              })}
                            </Box>
                          </Box>
                        )}
                        {data.additionalImages.length > 0 &&
                          data.deliveryOption !== 4 && (
                            <Box
                              sx={{ width: 200, height: 200, flexShrink: 0 }}
                            >
                              <Box
                                sx={{ fontWeight: 700, mb: 1 }}
                                component="div"
                              >
                                {t('donations.additional-information')}
                              </Box>
                              <Box
                                sx={{
                                  display: 'flex',
                                  gap: 1,
                                  width: '100%',
                                  height: 200,
                                  overflowX: 'auto',
                                  '&::-webkit-scrollbar': { display: 'none' },
                                }}
                              >
                                {data.additionalImages.map((image, idx) => {
                                  const isUrl = typeof image === 'string';
                                  const urlFromFile = isUrl
                                    ? getStoredImageUrl(image)
                                    : URL.createObjectURL(image.file);
                                  return (
                                    <Box
                                      key={`additional-${idx}`}
                                      component="img"
                                      src={urlFromFile}
                                      alt={`donation-additional-img-${idx}`}
                                      sx={{
                                        minWidth: 200,
                                        height: 200,
                                        objectFit: 'cover',
                                        borderRadius: 2,
                                      }}
                                    />
                                  );
                                })}
                              </Box>
                            </Box>
                          )}
                      </Box>

                      {/* Details Grid - More compact */}
                      <Grid container spacing={2}>
                        {/* Date & Time */}
                        <Grid size={{ xs: 12 }}>
                          <Box display="flex" alignItems="flex-start" gap={1}>
                            <Schedule
                              sx={{
                                color: 'action.active',
                                fontSize: 18,
                                mt: 0.5,
                              }}
                            />
                            <Box flex={1}>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Typography variant="body2" fontWeight="bold">
                                  {t('donations.date-and-time')}
                                </Typography>
                                <IconButton
                                  onClick={() =>
                                    setStep(stepIndices[STEP.TIME_RECURRING])
                                  }
                                  size="small"
                                >
                                  <Edit />
                                </IconButton>
                              </Box>
                              {dateTimeSummary?.rows?.length > 0 && (
                                <Stack spacing={0.75} sx={{ mt: 0.5 }}>
                                  {dateTimeSummary.rows.map((row) => (
                                    <Box
                                      key={`${row.label}-${row.dateText}-${row.timeText || ''}`}
                                      sx={{
                                        display: 'grid',
                                        gridTemplateColumns: {
                                          xs: '1fr',
                                          sm: '68px minmax(0, 1fr) auto',
                                        },
                                        alignItems: 'center',
                                        columnGap: 1,
                                        rowGap: 0.25,
                                        px: 1,
                                        py: 0.75,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: 1,
                                        bgcolor: 'grey.50',
                                      }}
                                    >
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{
                                          fontWeight: 700,
                                          textTransform: 'uppercase',
                                          letterSpacing: 0,
                                        }}
                                      >
                                        {row.label}
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        color="text.primary"
                                        sx={{
                                          fontSize: '0.875rem',
                                          fontWeight: 700,
                                          minWidth: 0,
                                        }}
                                      >
                                        {row.dateText}
                                      </Typography>
                                      {row.timeText && (
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                          sx={{
                                            fontSize: '0.875rem',
                                            justifySelf: {
                                              xs: 'start',
                                              sm: 'end',
                                            },
                                            whiteSpace: 'nowrap',
                                          }}
                                        >
                                          {row.timeText}
                                        </Typography>
                                      )}
                                    </Box>
                                  ))}
                                </Stack>
                              )}
                              {data.deliveryOption !== 4 &&
                                (data.occurrenceType === 'recurring' ||
                                  data.occurrenceType === 'indefinite') && (
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ fontSize: '0.875rem' }}
                                  >
                                    {data.occurrenceType === 'recurring' &&
                                      `Repeats: ${data.interval}`}
                                    {data.occurrenceType === 'indefinite' &&
                                      `Repeats: ${data.interval} · ${t('donations.no-end-date')}`}
                                    {(data.interval === 'Weekly' ||
                                      data.interval === 'Bi-weekly') &&
                                      Array.isArray(data.dayOfTheWeek) &&
                                      data.dayOfTheWeek.length > 0 && (
                                        <span>
                                          {' '}
                                          {t('donations.on')}{' '}
                                          {data.dayOfTheWeek
                                            .map((d) => daysOfTheWeek[d])
                                            .join(', ')}
                                        </span>
                                      )}
                                    {data.interval === 'Monthly' &&
                                      data.dayOfTheMonth && (
                                        <span>
                                          {' '}
                                          {t('donations.on')}{' '}
                                          {data.dayOfTheMonth.isLastDay
                                            ? t('donations.last-day')
                                            : data.dayOfTheMonth.customDay
                                              ? `day ${data.dayOfTheMonth.customDay}`
                                              : data.dayOfTheMonth.week &&
                                                  Number.isFinite(
                                                    data.dayOfTheMonth.week
                                                      .number
                                                  ) &&
                                                  Number.isFinite(
                                                    data.dayOfTheMonth.week
                                                      .dayOfTheWeek
                                                  )
                                                ? `${['1st', '2nd', '3rd', '4th', '5th'][data.dayOfTheMonth.week.number - 1]} ${daysOfTheWeek[data.dayOfTheMonth.week.dayOfTheWeek]}`
                                                : t('donations.custom-day')}
                                        </span>
                                      )}
                                  </Typography>
                                )}
                            </Box>
                          </Box>
                        </Grid>

                        {/* Pickup Location */}
                        <Grid size={{ xs: 12 }}>
                          <Box display="flex" alignItems="flex-start" gap={1}>
                            <LocationOn
                              sx={{
                                color: 'action.active',
                                fontSize: 18,
                                mt: 0.5,
                              }}
                            />
                            <Box flex={1}>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Typography variant="body2" fontWeight="bold">
                                  {t(
                                    data.deliveryOption === 4
                                      ? 'donations.staff-meal-location'
                                      : 'donations.pickup-location'
                                  )}
                                </Typography>
                                <IconButton
                                  onClick={() =>
                                    setStep(stepIndices[[STEP.DELIVERY_METHOD]])
                                  }
                                  size="small"
                                >
                                  <Edit />
                                </IconButton>
                              </Box>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ fontSize: '0.875rem' }}
                              >
                                {donorName ? `${donorName} (` : ''}
                                <Typography
                                  component="span"
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{
                                    fontSize: '0.875rem',
                                    ...(pickupAddressDiffers && {
                                      fontWeight: 'bold',
                                      textDecoration: 'underline',
                                    }),
                                  }}
                                >
                                  {data.pickupLocation?.formattedAddress ||
                                    t('donations.not-specified')}
                                </Typography>
                                {donorName ? ')' : ''}
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>

                        {/* Delivery Method */}
                        {data.deliveryOption !== 4 && (
                          <Grid size={{ xs: 12, sm: 6 }}>
                            <Box display="flex" alignItems="flex-start" gap={1}>
                              <LocationOn
                                sx={{
                                  color: 'action.active',
                                  fontSize: 18,
                                  mt: 0.5,
                                }}
                              />
                              <Box flex={1}>
                                <Typography variant="body2" fontWeight="bold">
                                  {t('donations.delivery-method')}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ fontSize: '0.875rem' }}
                                >
                                  {data.deliveryOption === 0
                                    ? t('donations.self-deliver')
                                    : t('donations.reciever-picks-up')}
                                </Typography>
                              </Box>
                            </Box>
                          </Grid>
                        )}

                        {/* Delivery Location — single recipient */}
                        {data.destLocation?.formattedAddress &&
                          data.deliveryOption !== 4 &&
                          !data.isPartialRescue && (
                            <Grid size={{ xs: 12 }}>
                              <Box
                                display="flex"
                                alignItems="flex-start"
                                gap={1}
                              >
                                <LocationOn
                                  sx={{
                                    color: 'action.active',
                                    fontSize: 18,
                                    mt: 0.5,
                                  }}
                                />
                                <Box flex={1}>
                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                  >
                                    <Typography
                                      variant="body2"
                                      fontWeight="bold"
                                    >
                                      {t('donations.delivery-location')}
                                    </Typography>
                                    <IconButton
                                      onClick={() =>
                                        setStep(stepIndices[STEP.LOGISTICS])
                                      }
                                      size="small"
                                    >
                                      <Edit />
                                    </IconButton>
                                  </Box>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ fontSize: '0.875rem' }}
                                  >
                                    {recipientName ? `${recipientName} (` : ''}
                                    <Typography
                                      component="span"
                                      variant="body2"
                                      color="text.secondary"
                                      sx={{
                                        fontSize: '0.875rem',
                                        ...(deliveryAddressDiffers && {
                                          fontWeight: 'bold',
                                          textDecoration: 'underline',
                                        }),
                                      }}
                                    >
                                      {data.destLocation?.formattedAddress ||
                                        t('donations.not-specified')}
                                    </Typography>
                                    {recipientName ? ')' : ''}
                                  </Typography>
                                  {selectedRecipientWarnings ? (
                                    <Typography
                                      variant="body2"
                                      color="warning.main"
                                      sx={{ fontSize: '0.875rem' }}
                                    >
                                      ⚠ {selectedRecipientWarnings}
                                    </Typography>
                                  ) : null}
                                </Box>
                              </Box>
                            </Grid>
                          )}

                        {/* Delivery Locations — partial rescue, per-recipient */}
                        {data.isPartialRescue &&
                          data.deliveryOption === 0 &&
                          Array.isArray(data.destinationAddresses) &&
                          data.destinationAddresses.length > 0 && (
                            <Grid size={{ xs: 12 }}>
                              <Box
                                display="flex"
                                alignItems="flex-start"
                                gap={1}
                              >
                                <LocationOn
                                  sx={{
                                    color: 'action.active',
                                    fontSize: 18,
                                    mt: 0.5,
                                  }}
                                />
                                <Box flex={1}>
                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                  >
                                    <Typography
                                      variant="body2"
                                      fontWeight="bold"
                                    >
                                      {t('donations.delivery-locations')}
                                    </Typography>
                                    <IconButton
                                      onClick={() =>
                                        setStep(
                                          stepIndices[STEP.SELECT_FOOD_TYPES]
                                        )
                                      }
                                      size="small"
                                    >
                                      <Edit />
                                    </IconButton>
                                  </Box>
                                  {data.destinationAddresses.map((addr) => {
                                    const name =
                                      recipientOptions.find(
                                        (r) => r.team_id === addr.teamId
                                      )?.name || addr.teamId;
                                    return (
                                      <Typography
                                        key={addr.teamId}
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ fontSize: '0.875rem' }}
                                      >
                                        {name}:{' '}
                                        {addr.location?.formattedAddress ||
                                          t('donations.not-specified')}
                                      </Typography>
                                    );
                                  })}
                                </Box>
                              </Box>
                            </Grid>
                          )}

                        {/* Vehicle/Driver */}
                        {data.deliveryOption !== 4 && (
                          <Grid size={{ xs: 12, sm: 6 }}>
                            <Box display="flex" alignItems="flex-start" gap={1}>
                              <DriveEta
                                sx={{
                                  color: 'action.active',
                                  fontSize: 18,
                                  mt: 0.5,
                                }}
                              />
                              <Box flex={1}>
                                <Box display="flex" alignItems="center" gap={1}>
                                  <Typography variant="body2" fontWeight="bold">
                                    {t('donations.transport')}
                                  </Typography>
                                  <IconButton
                                    onClick={() =>
                                      setStep(stepIndices[STEP.LOGISTICS])
                                    }
                                    size="small"
                                  >
                                    <Edit />
                                  </IconButton>
                                </Box>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ fontSize: '0.875rem' }}
                                >
                                  {t('donations.size-label')}:{' '}
                                  {vehicleTypes.find(
                                    (v) => v.value === data.vehicleSize
                                  )?.label || t('donations.not-specified')}
                                </Typography>
                                {(ENABLE_MULTIPLE_DRIVERS
                                  ? Array.isArray(data.drivers) &&
                                    data.drivers.length > 0
                                  : data.driverId &&
                                    data.driverId !== 'unassigned') && (
                                  <>
                                    <Typography
                                      variant="body2"
                                      fontWeight="bold"
                                      sx={{ mt: 1 }}
                                    >
                                      {ENABLE_MULTIPLE_DRIVERS &&
                                      Array.isArray(data.drivers) &&
                                      data.drivers.length > 1
                                        ? isFoodRun
                                          ? t('donations.runners')
                                          : t('donations.drivers')
                                        : isFoodRun
                                          ? t('donations.runner')
                                          : t('donations.driver-label')}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      sx={{ fontSize: '0.875rem' }}
                                    >
                                      {ENABLE_MULTIPLE_DRIVERS
                                        ? (data.drivers || [])
                                            .map(
                                              (d) =>
                                                drivers.find(
                                                  (driver) =>
                                                    driver.id === d.user_id
                                                )?.name
                                            )
                                            .filter(Boolean)
                                            .join(', ') ||
                                          t('donations.assigned')
                                        : drivers.find(
                                            (d) => d.id === data.driverId
                                          )?.name || t('donations.assigned')}
                                    </Typography>
                                  </>
                                )}
                              </Box>
                            </Box>
                          </Grid>
                        )}

                        {/* Instructions */}
                        {data.deliveryOption !== 4 &&
                          (isFoodRun ||
                            (!isFoodRun &&
                              ((data.deliveryOption === 1 &&
                                data.pickupInstructions &&
                                `${data.pickupInstructions}`.trim().length >
                                  0) ||
                                (data.deliveryOption === 0 &&
                                  data.dropoffInstructions &&
                                  `${data.dropoffInstructions}`.trim().length >
                                    0)))) && (
                            <Grid size={{ xs: 12 }}>
                              <Box
                                display="flex"
                                alignItems="flex-start"
                                gap={1}
                              >
                                <Description
                                  sx={{
                                    color: 'action.active',
                                    fontSize: 16,
                                    mt: 0.5,
                                  }}
                                />
                                <Box flex={1}>
                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                  >
                                    <Typography
                                      variant="body2"
                                      fontWeight="bold"
                                    >
                                      {t('donations.instructions')}
                                    </Typography>
                                    <IconButton
                                      onClick={() =>
                                        setStep(stepIndices[STEP.INSTRUCTIONS])
                                      }
                                      size="small"
                                    >
                                      <Edit />
                                    </IconButton>
                                  </Box>
                                  <Box
                                    display="flex"
                                    gap={2}
                                    mt={0.5}
                                    sx={{ flexWrap: 'wrap' }}
                                  >
                                    {(isFoodRun ||
                                      (data.deliveryOption === 1 &&
                                        data.pickupInstructions &&
                                        `${data.pickupInstructions}`.trim()
                                          .length > 0)) && (
                                      <Box flex={1} minWidth={200}>
                                        <Typography
                                          variant="body2"
                                          fontWeight="medium"
                                          sx={{ fontSize: '0.875rem' }}
                                        >
                                          {t('donations.pickup-label')}:
                                        </Typography>
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                          sx={{ fontSize: '0.875rem' }}
                                        >
                                          {data.pickupInstructions || ''}
                                        </Typography>
                                      </Box>
                                    )}
                                    {(isFoodRun ||
                                      (data.deliveryOption === 0 &&
                                        data.dropoffInstructions &&
                                        `${data.dropoffInstructions}`.trim()
                                          .length > 0)) && (
                                      <Box flex={1} minWidth={200}>
                                        <Typography
                                          variant="body2"
                                          fontWeight="medium"
                                          sx={{ fontSize: '0.875rem' }}
                                        >
                                          {t('donations.dropoff-label')}:
                                        </Typography>
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                          sx={{ fontSize: '0.875rem' }}
                                        >
                                          {data.dropoffInstructions || ''}
                                        </Typography>
                                      </Box>
                                    )}
                                  </Box>
                                </Box>
                              </Box>
                            </Grid>
                          )}
                      </Grid>
                    </CardContent>
                  </Card>
                );
              })()}
            </StepComponent>
          </StepContext.Provider>
        </DialogContent>

        <DialogActions sx={{ flexShrink: 0, px: 3, py: 2 }}>
          <Container
            sx={{
              ...(isStep(STEP.SELECT_FOOD_TYPES)
                ? {
                    maxWidth: '750px',
                  }
                : {}),
            }}
          >
            <Stack direction="row" spacing={2} width="100%">
              <Button
                type="button"
                fullWidth
                size="large"
                variant="outlined"
                color="inherit"
                disabled={loading || isWaitingForAnalysis}
                onClick={() => {
                  clearErrors();
                  if (step === 0) return onClose();
                  setStep((prv) => prv - 1);
                }}
              >
                {step === 0 ? t('common.cancel') : t('common.back')}
              </Button>

              {shouldShowSummaryButton && (
                <Button
                  type="button"
                  fullWidth
                  size="large"
                  variant="outlined"
                  color="primary"
                  onClick={goToSummary}
                >
                  {t('common.summary')}
                </Button>
              )}

              <LoadingButton
                type="submit"
                loading={loading || isWaitingForAnalysis}
                fullWidth
                size="large"
                variant="contained"
                color="secondary"
                disabled={
                  isStep(STEP.SELECT_FOOD_TYPES) &&
                  (!Array.isArray(data.foodTypeNames) ||
                    data.foodTypeNames.length === 0)
                }
              >
                {isWaitingForAnalysis
                  ? t('donations.analyzing-images')
                  : analysisError && isStep(STEP.UPLOAD_IMAGE)
                    ? t('donations.try-again')
                    : checkIfLastStep(step)
                      ? isFoodRun
                        ? t('donations.create-food-run')
                        : t('donations.create-donation')
                      : t('common.next')}
              </LoadingButton>
            </Stack>

            {null}
          </Container>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default DialogCreateDonation;
